import { Fragment, useEffect, useMemo, useState } from 'react'
import { DateField } from '../../components/ui/DateField'
import { AsOfField } from '../../components/ui/AsOfField'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Spinner } from '../../components/ui/Spinner'
import { EmailIncasariCard } from '../../components/vanzari/EmailIncasariCard'
import { useAuthStore, selectHasPermission } from '../../stores/auth'
import { FilaProfitabilitate } from './FilaProfitabilitate'
import { ETICHETE_CIFRE } from '@b4l/shared'

type Period = 'ytd' | 'month' | 'quarter' | 'last12'

interface Aging {
  current: number
  d0_30: number
  d31_60: number
  d61_90: number
  d90plus: number
}
interface AgentRow {
  agent: string
  clientsCount: number
  sellIn: number
  incasat: number
  restant: number
  /** Suma scadentelor clienților lui. */
  scadent?: number
  soldReconciled?: number | null
  aging?: Aging
  sellInSpv: number
  discrepancyPct: number | null
  maxOverdueDays: number | null
  target: number
  pctTarget: number | null
  spark: number[]
}
interface ClientRow {
  name: string
  agent: string
  grup: string | null
  cif: string | null
  sellIn: number
  incasat: number
  restant: number
  restantSource?: 'ea' | 'spv'
  aging?: Aging | null
  soldReconciled?: number | null
  anchorSold?: number | null
  facturiAfter?: number
  incasariAfter?: number
  /** Sold la „Data început" — tot ce s-a întâmplat de la ancoră până acolo, comprimat. */
  soldPornire?: number | null
  facturiPerioada: number
  incasariPerioada: number
  /** Din `incasariPerioada`, cât s-a stins în EA între „Data început" și ancoră. */
  incasariEa?: number
  /** Sold contabil EA la „Sold la data" — se recalculeaza din EA, nu e snapshotul la zi. */
  soldContabilAsOf?: number | null
  /** Cât din sold a ajuns exigibil. `null` = clientul n-are documente în EA, deci nu se poate spune. */
  scadent?: number | null
  /** Din ce calcul vine cifra de mai sus. Vezi `scadentSursa` pe server. */
  scadentSursa?: 'alocare' | 'ea' | null
  /** Cât lipsește ca descompunerea soldului de deschidere să se închidă. */
  scadentDiferenta?: number | null
  /** Cât din scadent vine din facturi fără termen agreat (scadența = ziua emiterii). */
  faraTermen?: { count: number; amount: number } | null
  /** Modul de plată — decide ce se arată în coloana „Scadent". */
  paymentMode?: 'SCADENTA' | 'PE_MASURA_VANZARII'
  /** Termenul de plată din EA, în zile. */
  paymTermDays?: number | null
  /** Acoperirea sell-out, la clienții care plătesc pe măsura vânzării. */
  sellout?: {
    pctQty: number
    pctVal: number
    stocEstimat: number
    fromMonth: string
    missingPeriods: number
    unpricedRows: number
    monthly: { month: string; qtyIn: number; qtyOut: number }[]
  } | null
  sellInSpv: number
  discrepancyPct: number | null
  maxOverdueDays: number | null
  target: number
  pctTarget: number | null
  lastOrderAt: string | null
  /** De unde vine „ultima comandă": comandă CRM trimisă, comandă ERP sau factură emisă. */
  lastOrderSource: 'crm' | 'erp' | 'factura' | null
  /**
   * Client care azi e la alt agent: rândul are doar vânzările facturate de `agent` în perioadă;
   * restanțele și ținta sunt la agentul de acum (`agentActual`).
   */
  fostClient?: boolean
  agentActual?: string | null
}

/** Eticheta rândului de vânzări al unui client trecut la alt agent. */
function FostClient({ c }: { c: ClientRow }) {
  if (!c.fostClient) return null
  return (
    <span
      className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500"
      title="Vânzările de mai jos le-a facturat acest agent. Restanțele, încasările și ținta sunt la agentul de acum."
    >
      fost client · acum la {c.agentActual ?? '—'}
    </span>
  )
}
interface Cockpit {
  period: Period
  months: string[]
  soldAsOf?: string | null
  anchor?: string | null
  asOf?: string | null
  /** „Data început" efectivă — cea cerută; limitată la ancoră doar dacă EA n-a răspuns. */
  startDate?: string | null
  /** Soldul de pornire vine direct din EA („Data început" la ancoră sau înainte). */
  startDinEa?: boolean
  canSetAnchor?: boolean
  /** Doar persoana care verifică față de balanță vede soldul contabil EA. */
  canSeeAccounting?: boolean
  /** Ultima zi acoperită de extrasele bancare încărcate (AAAA-LL-ZZ). */
  bankCoverageTo?: string | null
  /** Vizibilitatea pe portofoliu, decisă de server (vezi `lib/agentScope.ts`). */
  scope?: { agent: string | null; unmapped: boolean; canFilter: boolean }
  kpi: {
    sellIn: number
    incasat: number
    restant: number
    scadent: number
    clientiFaraTermen: number
    clientiScadenta: number
    clientiPeMasuraVanzarii: number
    sellInSpv: number
    clientsActive: number
    target: number
    pctTarget: number | null
    overdueClients: number
    inValuta?: { moneda: string; count: number; faraCurs?: number }[]
  }
  /** Grupurile de parteneri care există în portofolii. Din date, nu scrise în cod. */
  grupuri?: string[]
  agents: AgentRow[]
  clients: ClientRow[]
}

const PERIODS: { v: Period; label: string }[] = [
  { v: 'ytd', label: 'An curent (YTD)' },
  { v: 'month', label: 'Luna curentă' },
  { v: 'quarter', label: 'Trimestru' },
  { v: 'last12', label: 'Ultimele 12 luni' },
]

const lei = (n: number) => `${Math.round(n).toLocaleString('ro-RO')} lei`

/**
 * Suma exigibilă a unui client.
 *
 * `null` înseamnă „nu se poate spune" — clientul n-are documente în ExpertAccounts, de regulă
 * pentru că n-are cod fiscal completat. Se arată `—`, nu `0 lei`: un zero ar fi o afirmație pe
 * care n-o putem susține, iar agentul ar citi-o ca „nu-mi datorează nimic acum".
 */
function ScadentCell({ c, small }: { c: ClientRow; small?: boolean }) {
  const pad = small ? 'px-3 py-1.5' : 'px-3 py-1.5'

  // Clientul care plătește pe măsura vânzării n-are scadență în sens contabil. În locul cifrei
  // primește argumentul: cât din marfa livrată a vândut mai departe, și ce i-a mai rămas.
  if (c.paymentMode === 'PE_MASURA_VANZARII') {
    const s = c.sellout
    if (!s) {
      return (
        <td
          className={`${pad} text-right text-slate-400`}
          title="Nu avem niciun raport de sell-out"
        >
          fără raport
        </td>
      )
    }
    const luna = `${s.fromMonth.split('-')[1]}.${s.fromMonth.split('-')[0]}`
    const incredere =
      s.missingPeriods > 0
        ? `${s.missingPeriods} ${s.missingPeriods === 1 ? 'perioadă' : 'perioade'} fără raport`
        : null
    return (
      <td
        className={`${pad} text-right`}
        title={
          `Din marfa facturată din ${luna}: ${s.pctQty}% din bucăți, ${s.pctVal}% din valoare.` +
          (incredere ? ` ${incredere} — cifra e mai mică decât realitatea.` : '') +
          (s.unpricedRows > 0 ? ` ${s.unpricedRows} rânduri fără livrare în fereastră.` : '')
        }
      >
        <span className="font-medium text-slate-700">
          {s.pctQty}% buc · {s.pctVal}% val
        </span>
        <span className="block text-[10px] leading-tight text-slate-400">
          stoc ~{Math.round(s.stocEstimat).toLocaleString('ro-RO')} buc
          {incredere ? <span className="text-amber-600"> · {incredere}</span> : null}
        </span>
      </td>
    )
  }

  if (c.scadent == null) {
    return (
      <td className={`${pad} text-right text-slate-300`} title="Clientul n-are documente în EA">
        —
      </td>
    )
  }
  const fara = c.faraTermen
  /*
    Scadent NEGATIV = firma îi datorează LUI (stornouri peste facturi). Din 29.09.2026 nu se mai
    taie la zero: pe același client compensarea e legitimă — același analitic de 4111 —, iar un
    „—" ascundea chiar creditul. Se scrie verde și cu cuvântul lângă el, ca să nu fie cerut la
    telefon. Între clienți NU se compensează: totalul de sus rămâne cu plafon.
  */
  const credit = c.scadent < -0.005
  return (
    <td
      className={`${pad} text-right ${
        c.scadent > 0
          ? 'font-semibold text-rose-700'
          : credit
            ? 'font-medium text-emerald-700'
            : 'text-slate-400'
      }`}
      title={
        /*
          Sursa cifrei, PRIMA in text: cand pagina si fereastra se contrazic, asta e intrebarea
          dinaintea oricarei explicatii. Vezi `scadentSursa`.
        */
        c.scadentSursa === 'ea'
          ? `Din calculul vechi: descompunerea soldului de deschidere nu s-a închis pe sold${
              c.scadentDiferenta != null ? ` (diferență ${c.scadentDiferenta} lei)` : ''
            }. Cifra poate fi supraevaluată.`
          : credit
            ? 'Stornourile depășesc facturile scadente — firma îi datorează lui. Nu se scade din totalul de sus: între clienți nu se compensează.'
            : fara && fara.count > 0
              ? `Din care ${Math.round(fara.amount).toLocaleString('ro-RO')} lei din ` +
                `${fara.count} ${fara.count === 1 ? 'factură fără termen agreat' : 'facturi fără termen agreat'} ` +
                `(scadente din ziua emiterii) — partea pe care clientul o poate contesta`
              : 'Facturi cu termenul depășit'
      }
    >
      {c.scadent > 0 || credit ? lei(c.scadent) : '—'}
      {credit && <span className="ml-1 text-[10px] font-normal">credit</span>}
      {fara && fara.count > 0 ? <span className="ml-1 text-[10px] text-slate-400">*</span> : null}
    </td>
  )
}

/** Ultimele 12 sfârșituri de lună (YYYY-MM-DD), pt ancoră. */
function monthEndOptions(): { value: string; label: string }[] {
  const now = new Date()
  const out: { value: string; label: string }[] = []
  const months = [
    'ian',
    'feb',
    'mar',
    'apr',
    'mai',
    'iun',
    'iul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ]
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i + 1, 0)
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    out.push({ value, label: `${months[d.getMonth()]} ${d.getFullYear()}` })
  }
  return out
}
const MONTH_ENDS = monthEndOptions()

function Spark({ data }: { data: number[] }) {
  const max = Math.max(1, ...data)
  const w = 96
  const h = 26
  const n = data.length
  const pts = data
    .map((v, i) => `${(i / Math.max(1, n - 1)) * w},${h - (v / max) * (h - 3) - 1.5}`)
    .join(' ')
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke="#0b4f6c" strokeWidth={1.6} />
    </svg>
  )
}

/**
 * Termenul de plata al clientului: „30 zile", „60 zile"… iar „PMV" la plata pe masura vanzarii
 * (termenul de 90 de zile inseamna intotdeauna PMV — Alexandru, 08.10.2026).
 */
function TermenPlata({ c }: { c: Pick<ClientRow, 'paymentMode' | 'paymTermDays'> }) {
  if (c.paymentMode === 'PE_MASURA_VANZARII') {
    return (
      <span
        className="rounded bg-violet-50 px-1.5 py-0.5 font-medium text-violet-700"
        title="Pe măsura vânzării"
      >
        PMV
      </span>
    )
  }
  return <>{c.paymTermDays != null ? `${c.paymTermDays} zile` : '—'}</>
}

function OverdueDot({ days }: { days: number | null }) {
  const color = days == null || days <= 0 ? '#16a34a' : days > 30 ? '#dc2626' : '#d97706'
  const label = days == null || days <= 0 ? 'OK' : `${days}z`
  return (
    <span className="inline-flex items-center gap-1">
      <span style={{ background: color }} className="inline-block w-2.5 h-2.5 rounded-full" />
      <span className="text-xs text-slate-500">{label}</span>
    </span>
  )
}

interface OpenInvoice {
  id: number
  docId: string | null
  issueDate: string | null
  effectiveDueDate: string | null
  totalWithTax: number
  paidAmount: number
  remainingAmount: number
  daysToDue: number | null
}

interface CompareRow {
  doc: string
  inEa: boolean
  inSpv: boolean
  eaTotal: number | null
  spvTotal: number | null
  date: string | null
  status: 'ok' | 'only_ea' | 'only_spv' | 'value_diff' | 'before_spv'
}
interface CompareResp {
  rows: CompareRow[]
  eaTotal: number
  spvTotal: number
  onlyEa: number
  onlySpv: number
  /** Facturi mai vechi decât copia locală SPV — nu sunt neconcordanțe. */
  beforeSpv: number
  /** Prima zi acoperită de copia locală SPV (AAAA-LL-ZZ). */
  spvCoverageFrom: string | null
}

interface ReconLine {
  docNo: string
  date: string
  total: number
  isStorno: boolean
}
interface PaymentLine {
  date: string
  amount: number
  partnerName: string | null
  /**
   * Explicația din extras — referința băncii sau descrierea operațiunii. Serverul o trimite de
   * mult; interfața n-o cerea. În fișa de cont e singurul lucru care distinge două încasări din
   * aceeași zi.
   */
  reference?: string | null
  /** `ea` = suma stinsă în contabilitate înainte de ancoră, nu o operațiune din extras. */
  sursa?: 'ea' | 'extras'
}
interface AlocareLinie {
  /** Documentul vine din soldul de deschidere, nu din facturile perioadei. */
  dinDeschidere?: boolean
  dueDate?: string | null
  docNo: string
  date: string | null
  total: number
  paid: number
  rest: number
  isStorno: boolean
  isSoldInitial?: boolean
}
interface ReconResp {
  /** Alocarea FIFO, calculată pe server — aceeași care intră în extrasul de cont. */
  alocare: { linii: AlocareLinie[]; avansRamas: number }
  anchor: string
  asOf: string | null
  /** „Data început" efectivă — de aici încolo se listează facturile și încasările. */
  startDate: string | null
  anchorSold: number
  soldPornire: number
  invoices: ReconLine[]
  invoicesTotal: number
  payments: PaymentLine[]
  paymentsTotal: number
  /** Din `paymentsTotal`, cât s-a stins în EA între „Data început" și ancoră. */
  eaSettled?: number
  soldReconciled: number
  /**
   * Scadentul, din ACEEAȘI funcție care alimentează coloana din tabelul mare — nu recalculat aici.
   * `null` când nu s-a putut afla (client fără cod fiscal, EA indisponibil).
   */
  scadent?: {
    scadent: number
    faraTermen: { count: number; amount: number }
    documente: {
      docNo: string
      dueDate: string | null
      ramas: number
      esteScadent: boolean
      /** Cât din diferența EA−reconciliat s-a imputat pe documentul ăsta. Vezi `ScadentCelula`. */
      stins: number
    }[]
  } | null
}

/**
 * O suma care se SCADE, cu semnul potrivit: „−709 lei", iar cand e negativa „+709 lei".
 * Fara asta, un minus in fata unei sume deja negative iesea „−-709 lei".
 */
function scade(v: number): string {
  return v < 0 ? `+${lei(-v)}` : `−${lei(v)}`
}

/** dd.mm.yyyy pentru afisare romaneasca. */
function dmy(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}.${m}.${y}`
}

/**
 * Incasarile asa cum au intrat in banca: zi si suma, luate ca atare din extras. Raspunde
 * la „cati bani au intrat pe 14.07", intrebare la care alocarea pe facturi nu raspunde.
 * Totalul afisat e suma randurilor; daca nu coincide cu cel folosit in formula, o spunem.
 */
function PaymentsList({ payments, total }: { payments: PaymentLine[]; total: number }) {
  const [open, setOpen] = useState(false)
  if (payments.length === 0) return null
  const sum = payments.reduce((s, p) => s + p.amount, 0)
  const mismatch = Math.abs(sum - total) > 0.01
  // Mai multe incasari in aceeasi zi se aduna — utila e ziua, nu tranzactia.
  const byDay = new Map<string, number>()
  for (const p of payments) byDay.set(p.date, (byDay.get(p.date) ?? 0) + p.amount)
  const days = [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1))

  return (
    <div className="mt-2 rounded border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
      >
        <span>
          {open ? '▾' : '▸'} Încasări efective din extras — {days.length}{' '}
          {days.length === 1 ? 'zi' : 'zile'}, {payments.length}{' '}
          {payments.length === 1 ? 'operațiune' : 'operațiuni'}
        </span>
        <span className="font-medium text-emerald-700">{lei(sum)}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 px-3 py-2">
          {mismatch && (
            <div className="mb-1.5 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
              Suma din extras ({lei(sum)}) nu coincide cu cea folosită în calcul ({lei(total)}).
            </div>
          )}
          <table className="w-full text-xs">
            <tbody>
              {days.map(([d, amt]) => (
                <tr key={d} className="border-b border-slate-100 last:border-0">
                  <td className="py-1 text-slate-600">{dmy(d)}</td>
                  <td className="py-1 text-right tabular-nums font-medium text-emerald-700">
                    {lei(amt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * Facturile din reconciliere, cu INCASARILE alocate FIFO: soldul de la ancora si facturile
 * (cele mai vechi intai) se sting din banii intrati in cont. Totalul „Rest" coincide exact
 * cu soldul reconciliat.
 *
 * Facturile negative (storno, note de credit) NU sting alte facturi: sunt facturi obisnuite,
 * doar cu semn minus, si raman in tabel cu restul lor negativ, diminuand totalul. Inainte
 * intrau intr-un „bazin" impreuna cu incasarile, iar cand nu aveau ce stinge apareau ca o
 * linie „Avans / credit ramas" — un tratament diferit, care nu se intelegea din tabel.
 */
/**
 * Descarca extrasul de cont PDF. Trece prin `api` ca sa poarte tokenul de autentificare —
 * un `window.open` pe URL ar primi 401.
 */
/**
 * „Sold facturi" — ce a rămas deschis, document cu document.
 *
 * Al doilea document care pleacă spre client. Extrasul de cont răspunde la „cum s-a ajuns la
 * soldul ăsta"; acesta la „ce anume mai am de plătit". Cifrele: contabilitatea până la data de
 * pornire, apoi facturile și extrasele bancare — același sold ca fereastra.
 */
async function descarcaSoldFacturi(
  client: { name: string; cif: string | null },
  asOf: string,
  startDate: string,
  setBusy: (v: boolean) => void,
) {
  setBusy(true)
  try {
    const params = new URLSearchParams({ name: client.name })
    if (client.cif) params.set('cif', client.cif)
    // Aceeasi perioada ca fereastra: documentul trebuie sa spuna acelasi sold ca ecranul.
    if (asOf) params.set('asOf', asOf)
    if (startDate) params.set('startDate', startDate)
    const res = await api.get(`/agents/client-sold-facturi/pdf?${params.toString()}`, {
      responseType: 'blob',
      timeout: 60_000,
    })
    const url = URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sold-facturi-${client.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch {
    toast.error('Nu am putut genera situația facturilor')
  } finally {
    setBusy(false)
  }
}

async function descarcaExtras(
  client: { name: string; cif: string | null },
  asOf: string,
  startDate: string,
  setBusy: (v: boolean) => void,
) {
  setBusy(true)
  try {
    const params = new URLSearchParams({ name: client.name })
    if (client.cif) params.set('cif', client.cif)
    if (asOf) params.set('asOf', asOf)
    if (startDate) params.set('startDate', startDate)
    const res = await api.get(`/agents/client-reconciliation/pdf?${params.toString()}`, {
      responseType: 'blob',
      timeout: 60_000,
    })
    const url = URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `extras-cont-${client.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch {
    toast.error('Nu am putut genera extrasul de cont')
  } finally {
    setBusy(false)
  }
}

function ReconTable({ recon }: { recon: ReconResp }) {
  const { linii, avansRamas: leftover } = recon.alocare
  /*
    „Sold la ..." mai e un BLOC doar cand EA n-a dat componenta. Din 30.09.2026 documentele lui
    vin unul câte unul și se sting FIFO pe scadență, ca oricare altul — vezi `alocaIncasari`.
  */
  const deschidere = linii.find((l) => l.isSoldInitial)

  /*
    Fila asta e DESCOMPUNEREA soldului de încasat, nu un istoric.

    Alexandru, 29.09.2026: „pe coloana Rest vreau să văd componenta acestui sold (...) facturile
    care sunt închise nu trebuie afișate deloc". Până acum se listau toate facturile din
    perioadă, inclusiv cele încasate integral — rânduri cu Rest 0, care nu compun nimic și fac
    lista de două ori mai lungă decât întrebarea.

    Rămân și rândurile NEGATIVE: un storno deschis scade din sold, deci e parte din răspuns.
  */
  const toate = linii.filter((l) => !l.isSoldInitial)
  const rows = toate.filter((l) => Math.abs(l.rest) >= 0.005)
  const ascunse = toate.length - rows.length

  /*
    Suma REALĂ a coloanei, nu `soldReconciled` scris din nou în subsol.

    Invariantul — „suma coloanei Rest formează soldul" — trebuie să se poată VEDEA, nu crede pe
    cuvânt. Dacă vreodată cele două se despart, rândul de mai jos o spune în loc s-o ascundă.
  */
  const sumaRest =
    Math.round((rows.reduce((s, r) => s + r.rest, 0) + (deschidere?.rest ?? 0)) * 100) / 100
  const diferentaFataDeSold = Math.round((sumaRest - (recon.soldReconciled ?? 0)) * 100) / 100

  // Documentele contabile, după numărul lor — de acolo vin scadența și scadentul. Fereastra
  // listează facturile emise după ancoră; scadentul se judecă pe TOATE documentele deschise,
  // deci unele rânduri n-au corespondent (stinse între timp) și invers.
  const dupaNumar = new Map((recon.scadent?.documente ?? []).map((d) => [d.docNo, d]))
  const doc = (n: string) => dupaNumar.get(n)

  /*
    Cat din scadent vine din documente care NU apar in fereastra. Se citeste din ALOCARE, cu
    exact predicatul celulei de pe rand (`ScadentCelula`) — altfel cifra ar fi o scadere intre
    doua calcule diferite, adica numarul care se ajusteaza singur ca sa iasa totalul. Alexandru,
    30.09.2026, despre exact asa ceva: „cum ai calculat suma de 6869?"

    In mod normal iese ZERO: din 30.09.2026 documentele soldului de deschidere sunt randuri aici,
    marcate „din sold initial". Ramane pe ecran doar cand EA n-a dat componenta si deschiderea e
    inca un bloc.
  */
  const refScadent = recon.asOf ? new Date(`${recon.asOf}T23:59:59`).getTime() : Date.now()
  const eScadenta = (l: { dueDate?: string | null; date: string | null }) => {
    const t = l.dueDate ?? l.date
    return !!t && new Date(t).getTime() <= refScadent
  }
  const scadentInFereastra = rows.reduce((s, r) => s + (eScadenta(r) ? r.rest : 0), 0)
  const scadentInAfara =
    Math.round(((recon.scadent?.scadent ?? 0) - scadentInFereastra) * 100) / 100
  return (
    <table className="w-full text-sm">
      <thead className="text-slate-500 border-b border-slate-200">
        <tr>
          <th className="text-left py-1.5">Document</th>
          <th className="text-left py-1.5">Dată</th>
          <th className="text-left py-1.5">Scadență</th>
          <th className="text-right py-1.5">Total</th>
          <th className="text-right py-1.5">Încasat</th>
          <th className="text-right py-1.5">Rest</th>
          {/*
            Titlul poarta si legenda celor trei stari, fiindca ele se confunda: „—" = termenul
            n-a venit; o cifra = atat se cere; „acoperită" = termenul a trecut, dar suma e deja
            stinsa din incasari nealocate.
          */}
          <th
            className="text-right py-1.5"
            title={
              'Cât se mai cere pe documentul ăsta. „—" = termenul n-a trecut încă. ' +
              'O cifră = atât e de cerut. „acoperită" = termenul a trecut, dar suma e stinsă ' +
              'din încasări intrate în bancă și nealocate în contabilitate.'
            }
          >
            Scadent
          </th>
        </tr>
      </thead>
      <tbody>
        {deschidere && (
          <tr className="border-b border-slate-100 bg-slate-50/60">
            <td className="py-1.5 font-medium text-slate-700">{deschidere.docNo}</td>
            <td className="py-1.5 text-slate-500">—</td>
            {/* Soldul de deschidere e o sumă, nu un document: n-are termen propriu. */}
            <td className="py-1.5 text-slate-400">—</td>
            <td className="py-1.5 text-right">{lei(deschidere.total)}</td>
            <td className="py-1.5 text-right text-slate-500">{lei(deschidere.paid)}</td>
            <td className="py-1.5 text-right font-medium text-amber-700">{lei(deschidere.rest)}</td>
            {/*
              Cât e scadent DIN SOLDUL INIȚIAL. Scria „—", iar coloana nu se aduna la „Sold
              scadent" din subsol — nu se înțelegea din ce e formată restanța (Alexandru,
              29.09.2026).

              Cifra e chiar `scadentInAfara`: scadentul total minus cel de pe documentele din
              fereastră. Soldul inițial ESTE comprimarea acelor documente vechi, deci partea lui
              scadentă e exact atât. Se întâmplă doar când EA n-a dat componenta deschiderii;
              altfel documentele sunt rânduri aici, marcate „din sold inițial".
            */}
            <td
              className={`py-1.5 text-right ${
                scadentInAfara > 0 ? 'font-medium text-rose-700' : 'text-slate-300'
              }`}
              title="Scadentul documentelor dinainte de „Data început”, comprimate în soldul inițial"
            >
              {Math.abs(scadentInAfara) > 0.005 ? lei(scadentInAfara) : '—'}
            </td>
          </tr>
        )}
        {rows.map((r) => (
          <tr key={r.docNo} className="border-b border-slate-100">
            <td className="py-1.5 font-medium text-slate-700">
              {r.docNo}
              {r.isStorno && <span className="ml-1 text-[10px] text-emerald-700">storno</span>}
              {/* Din ce sold vine: altfel n-ai cum să știi ce e mai vechi decât „Data început". */}
              {r.dinDeschidere && (
                <span
                  className="ml-1 text-[10px] text-slate-400"
                  title="Document din soldul de deschidere, emis înainte de „Data început”"
                >
                  din sold inițial
                </span>
              )}
            </td>
            <td className="py-1.5 text-slate-500">{r.date ? dmy(r.date) : '—'}</td>
            {/* Termenul, din ACEEASI linie de alocare din care se judeca si scadentul. */}
            <td className="py-1.5 text-slate-500">
              {(r.dueDate ?? doc(r.docNo)?.dueDate) ? (
                <>
                  {dmy((r.dueDate ?? doc(r.docNo)!.dueDate)!)}
                  {(r.dueDate ?? doc(r.docNo)!.dueDate)!.slice(0, 10) ===
                    (r.date ?? '').slice(0, 10) && (
                    <span
                      className="ml-1 text-[10px] text-amber-600"
                      title="Scadența e chiar ziua emiterii — factura n-a avut termen agreat"
                    >
                      *
                    </span>
                  )}
                </>
              ) : (
                <span className="text-slate-300" title="Documentul nu mai e deschis în EA">
                  —
                </span>
              )}
            </td>
            <td className={`py-1.5 text-right ${r.total < 0 ? 'text-emerald-700' : ''}`}>
              {lei(r.total)}
            </td>
            <td className="py-1.5 text-right text-slate-500">{r.paid > 0 ? lei(r.paid) : '—'}</td>
            <td
              className={`py-1.5 text-right font-medium ${r.rest < 0 ? 'text-emerald-700' : r.rest > 0 ? 'text-amber-700' : 'text-slate-400'}`}
            >
              {lei(r.rest)}
            </td>
            {/*
              DE CE „Rest" și „Scadent" pot spune lucruri diferite despre același document.

              Alexandru, 29.09.2026, despre SRT109402: „de ce nu apare restanța?" — Rest scria
              7.925 lei, Scadent scria 0. Nu e o contradicție, sunt două calcule diferite:
              „Rest" alocă încasările din EXTRASELE BANCARE, „Scadent" stinge FIFO diferența
              dintre soldul contabil EA și cel reconciliat — adică banii intrați în bancă și
              nealocați încă pe facturi în contabilitate.

              La clientul acela diferența era ~77.000 lei, iar SRT109402 (10.08) a intrat printre
              cele mai vechi. Explicația trebuie să stea PE RÂND: altfel întrebarea revine la
              fiecare client cu punctajul în urmă.
            */}
            <ScadentCelula linie={r} laData={recon.asOf} />
          </tr>
        ))}
        {leftover > 0 && (
          <tr className="border-b border-slate-100 bg-emerald-50/40">
            <td className="py-1.5 font-medium text-emerald-800">Avans / credit rămas</td>
            <td className="py-1.5 text-slate-500">—</td>
            <td className="py-1.5 text-slate-400">—</td>
            <td className="py-1.5 text-right text-slate-400">—</td>
            <td className="py-1.5 text-right text-slate-500">{lei(leftover)}</td>
            <td className="py-1.5 text-right font-medium text-emerald-700">{lei(-leftover)}</td>
            <td className="py-1.5 text-right text-slate-300">—</td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr className="border-t-2 border-slate-300 font-semibold">
          <td className="py-2" colSpan={5}>
            Sold de încasat
          </td>
          <td className="py-2 text-right text-amber-700">{lei(sumaRest)}</td>
          <td className="py-2"></td>
        </tr>
        {/* Plasa de siguranță: dacă suma coloanei nu mai dă soldul, se vede aici, nu la control. */}
        {Math.abs(diferentaFataDeSold) > 0.005 && (
          <tr>
            <td colSpan={7} className="pt-1 text-[11px] font-normal text-rose-700">
              Suma coloanei „Rest” diferă cu {lei(diferentaFataDeSold)} de soldul reconciliat (
              {lei(recon.soldReconciled)}). Nu ar trebui — semnalează-mi.
            </td>
          </tr>
        )}
        {ascunse > 0 && (
          <tr>
            <td colSpan={7} className="pt-1 text-[11px] font-normal text-slate-400">
              {ascunse === 1
                ? 'O factură încasată integral nu e afișată'
                : `${ascunse} facturi încasate integral nu sunt afișate`}{' '}
              — lista arată doar ce compune soldul de mai sus.
            </td>
          </tr>
        )}
        {recon.scadent && (
          <tr className="font-semibold">
            <td className="py-1" colSpan={5}>
              Sold scadent
              <span className="ml-2 font-normal text-[11px] text-slate-400">
                facturi cu termenul depășit, după stingerea încasărilor nealocate
              </span>
            </td>
            <td className="py-1"></td>
            {/*
              Negativ = firma îi datoreaza LUI. Se compensează doar între documentele aceluiași
              partener — același analitic de 4111 —, nu între clienți. Cuvântul „credit" stă
              lângă cifră ca agentul să n-o ceară la telefon.
            */}
            <td
              className={`py-1 text-right ${
                recon.scadent.scadent < 0 ? 'text-emerald-700' : 'text-rose-700'
              }`}
            >
              {lei(recon.scadent.scadent)}
              {recon.scadent.scadent < 0 && (
                <span className="ml-1 text-[10px] font-normal">credit</span>
              )}
            </td>
          </tr>
        )}
        {recon.scadent && Math.abs(scadentInAfara) > 0.5 && (
          <tr>
            <td colSpan={7} className="pt-1 text-[11px] font-normal text-slate-400">
              Din care {lei(scadentInAfara)} din documente emise înainte de {'„Data început"'}, care
              nu apar în lista de mai sus. Se văd unul câte unul în tabul {'„Fișă de cont"'}, în
              fișa din contabilitate.
            </td>
          </tr>
        )}
        {recon.scadent && recon.scadent.faraTermen.count > 0 && (
          <tr>
            <td colSpan={7} className="pt-1 text-[11px] font-normal text-amber-700">
              Din care {lei(recon.scadent.faraTermen.amount)} din {recon.scadent.faraTermen.count}{' '}
              {recon.scadent.faraTermen.count === 1 ? 'factură fără' : 'facturi fără'} termen agreat
              (marcate cu *) — scadente din ziua emiterii, deci partea contestabilă.
            </td>
          </tr>
        )}
      </tfoot>
    </table>
  )
}

/**
 * Celula „Scadent" dintr-un rând al reconcilierii, cu explicația pentru ce s-a stins.
 *
 * `stins` e cât din diferența EA−reconciliat s-a imputat pe documentul ăsta. Când e mai mare ca
 * zero, cifra din „Scadent" e mai mică decât „Rest" — și fără o vorbă lângă ea pare o greșeală.
 */
function ScadentCelula({
  linie,
  laData,
}: {
  linie: { dueDate?: string | null; date: string | null; rest: number }
  laData: string | null
}) {
  /*
    Scadentul unui rand se CITESTE din alocare: termenul a trecut si a ramas ceva de cerut.
 
    Venea din `recon.scadent.documente`, adica din alt calcul — cel care imputa a doua oara
    aceiasi bani. De aceea acelasi rand putea spune „neincasata" pe Rest si „acoperita" pe
    Scadent. Acum cele doua coloane citesc din aceeasi sursa, deci nu se mai pot contrazice.
  */
  const t = linie.dueDate ?? linie.date
  const ref = laData ? new Date(`${laData}T23:59:59`).getTime() : Date.now()
  const esteScadent = !!t && new Date(t).getTime() <= ref
  const d = esteScadent ? { esteScadent, ramas: linie.rest, stins: 0 } : undefined
  return <ScadentCelulaVeche d={d} />
}

function ScadentCelulaVeche({ d }: { d?: { esteScadent: boolean; ramas: number; stins: number } }) {
  if (!d?.esteScadent) {
    /* Termenul n-a venit încă. „—" e singura stare în care documentul NU e scadent. */
    return (
      <td className="py-1.5 text-right text-slate-300" title="Termenul n-a trecut încă">
        —
      </td>
    )
  }
  const acoperit = d.stins > 0.005
  const explicatie = acoperit
    ? `Acoperită din încasări nealocate: ${Math.round(d.stins).toLocaleString('ro-RO')} lei. ` +
      'Sunt bani intrați în bancă pe care contabilitatea nu i-a pus încă pe facturi. ' +
      'De aceea „Scadent” e mai mic decât „Rest” — nu cerem bani deja primiți.'
    : 'Termenul a trecut și suma e nestinsă'

  /*
    „0 lei" si „—" aratau la fel de tacut si insemnau lucruri OPUSE.

    Alexandru, 29.09.2026, despre SRT109402: „apare ca nu e scadenta, cum explici asta?" — dar
    cifra 0 spunea tocmai ca ESTE scadenta, doar ca nu mai e nimic de cerut pe ea. Documentele
    nescadente arata „—". Un zero gol lasa cititorul sa aleaga singur intelesul, si a ales pe
    dos. De aceea aici scrie un CUVANT, nu o cifra.
  */
  if (Math.abs(d.ramas) < 0.005) {
    return (
      <td className="py-1.5 text-right text-xs font-medium text-emerald-700" title={explicatie}>
        acoperită
      </td>
    )
  }

  return (
    <td className="py-1.5 text-right font-medium text-rose-700" title={explicatie}>
      {lei(d.ramas)}
      {acoperit && (
        <span
          className="ml-1 text-[10px] font-normal text-slate-400"
          title="Cât s-a imputat din încasările nealocate"
        >
          −{Math.round(d.stins).toLocaleString('ro-RO')}
        </span>
      )}
    </td>
  )
}

interface FacturiEa {
  laData: string
  soldEa: number
  scadent: number
  faraTermen: { count: number; amount: number }
  stinsTotal: number
  documente: {
    docNo: string
    docDate: string
    dueDate: string | null
    remaining: number
    /** La storno: factura pe care o anulează. */
    anuleaza?: string | null
    /** Cât s-a compensat între storno și factura lui, înainte de stingere. */
    compensat?: number
    stins: number
    ramas: number
    esteScadent: boolean
    faraTermen: boolean
  }[]
}

/**
 * Facturile contabile deschise, în ordinea în care le stinge FIFO.
 *
 * Coloana „Stins" e miezul: arată cât din diferența dintre soldul contabil și cel reconciliat cu
 * banca s-a imputat pe fiecare document. Fără ea, scadentul ar fi o cifră care apare din senin.
 */
/**
 * FISA DE CONT pe perioada aleasa: sold de deschidere, apoi facturi si incasari, cronologic.
 *
 * Alexandru, 30.09.2026: „daca selectez Data inceput 01.01.2026, vom prelua EaOpenInvoices la
 * 31.12.2025, apoi toate facturile emise si toate incasarile incepand cu 01.01.2026, corect?"
 * — corect, si toate trei existau deja in reconciliere:
 *
 *   `soldPornire` = soldul EA la ziua dinaintea „Data inceput";
 *   `invoices`    = facturile din `EaInvoiceSnapshot` de dupa acea zi — din EA, nu din SPV;
 *   `payments`    = incasarile din extrasele bancare.
 *
 * Prima incercare a construit fisa peste `EaOpenInvoice`, adica peste documentele DESCHISE de
 * azi. Se inchidea pe soldul contabil, dar nu era o fisa pe perioada: facturile achitate in
 * perioada lipseau cu totul.
 *
 * SE INCHIDE PE SOLDUL RECONCILIAT, nu pe cel contabil EA, fiindca incasarile vin din banca.
 * Diferenta dintre ele sunt banii intrati si nepunctati in ERP — se vede, nu se ascunde.
 */
function FisaDeCont({ recon }: { recon: ReconResp }) {
  const deLa = recon.startDate ?? recon.anchor
  type Rand = {
    cheie: string
    data: string
    document: string
    detaliu: string | null
    debit: number
    credit: number
  }

  const randuri: Rand[] = [
    ...recon.invoices.map((f, i) => ({
      cheie: `f${i}-${f.docNo}`,
      data: f.date.slice(0, 10),
      document: f.docNo,
      detaliu: f.isStorno ? 'storno' : null,
      /* O factura negativa (storno) nu e o incasare: ramane pe debit, cu semn minus. */
      debit: f.total,
      credit: 0,
    })),
    ...recon.payments.map((p, i) => ({
      cheie: `p${i}-${p.date}`,
      data: p.date,
      /* Linia `ea` nu e o operațiune din extras: e ce s-a stins în ERP înainte de ancoră. */
      document: p.sursa === 'ea' ? 'Stins în contabilitate' : 'Încasare',
      detaliu: p.reference ?? p.partnerName,
      debit: 0,
      credit: p.amount,
    })),
  ].sort((a, b) => a.data.localeCompare(b.data) || a.cheie.localeCompare(b.cheie))

  let sold = recon.soldPornire
  const cuSold = randuri.map((r) => {
    sold = Math.round((sold + r.debit - r.credit) * 100) / 100
    return { ...r, sold }
  })
  /* Fisa trebuie sa se inchida pe soldul de incasat. Daca nu, se vede aici, nu la control. */
  const diferenta = Math.round((sold - (recon.soldReconciled ?? 0)) * 100) / 100

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-2 py-1.5 text-left">Document</th>
            <th className="px-2 py-1.5 text-center">Data</th>
            <th className="px-2 py-1.5 text-right">Debit</th>
            <th className="px-2 py-1.5 text-right">Credit</th>
            <th className="px-2 py-1.5 text-right">Sold</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-200 bg-slate-50">
            <td className="px-2 py-1 font-medium text-slate-700" colSpan={2}>
              Sold la {dmy(deLa)}
            </td>
            <td className="px-2 py-1 text-right">—</td>
            <td className="px-2 py-1 text-right">—</td>
            <td className="px-2 py-1 text-right font-medium tabular-nums">
              {lei(recon.soldPornire)}
            </td>
          </tr>
          {cuSold.map((r) => (
            <tr key={r.cheie} className="border-b border-slate-100">
              <td className="px-2 py-1 text-slate-700">
                {r.document}
                {r.detaliu && (
                  <span className="block max-w-[22rem] truncate text-[10px] text-slate-400">
                    {r.detaliu}
                  </span>
                )}
              </td>
              <td className="px-2 py-1 text-center text-slate-500">{dmy(r.data)}</td>
              <td className="px-2 py-1 text-right">{r.debit !== 0 ? lei(r.debit) : '—'}</td>
              <td className="px-2 py-1 text-right text-emerald-700">
                {r.credit !== 0 ? lei(r.credit) : '—'}
              </td>
              <td className="px-2 py-1 text-right font-medium tabular-nums text-slate-700">
                {lei(r.sold)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-300 font-semibold">
            <td className="px-2 py-2" colSpan={4}>
              Sold de încasat{recon.asOf ? ` la ${dmy(recon.asOf)}` : ''}
            </td>
            <td className="px-2 py-2 text-right text-amber-700">{lei(sold)}</td>
          </tr>
          {Math.abs(diferenta) > 0.005 && (
            <tr>
              <td colSpan={5} className="pt-1 text-[11px] font-normal text-rose-700">
                Fișa se închide pe {lei(sold)}, dar soldul reconciliat e {lei(recon.soldReconciled)}{' '}
                — diferență {lei(diferenta)}. Nu ar trebui.
              </td>
            </tr>
          )}
        </tfoot>
      </table>
      <p className="mt-2 text-[11px] text-slate-500">
        Soldul de deschidere e cel din contabilitate la {dmy(deLa)}. Facturile vin din EA,
        încasările din extrasele bancare — de aceea fișa se închide pe soldul{' '}
        <strong>reconciliat</strong>, nu pe cel contabil EA. Diferența dintre ele sunt banii intrați
        în cont pe care contabilitatea nu i-a pus încă pe facturi.
      </p>
    </div>
  )
}

/**
 * Puntea dintre cele doua fise: a noastra si cea din contabilitate.
 *
 * Alexandru, 30.09.2026: „sa fie fisa de cont care sa se coreleze cu fisa din contabilitate".
 * Cele doua NU inchid pe aceeasi cifra, si nici n-au cum: fisa de sus ia facturile din EA dar
 * incasarile din EXTRASE, deci e inaintea contabilitatii cu exact banii pe care contabilul
 * inca nu i-a pus pe facturi. Diferenta nu e o eroare — e o lista de lucru.
 *
 * Nota de subsol o spunea deja in cuvinte. In cifre se bifeaza: pui fisa din EA alaturi si
 * vezi ca se intalnesc.
 */
function PuntaContabilitate({ recon, soldEa }: { recon: ReconResp; soldEa: number }) {
  const sold = recon.soldReconciled ?? 0
  const diferenta = Math.round((soldEa - sold) * 100) / 100
  if (Math.abs(diferenta) < 0.005) {
    return (
      <p className="mt-4 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800">
        Fișa noastră și cea din contabilitate închid pe aceeași cifră — {lei(soldEa)}. Tot ce a
        intrat în bancă e pus pe facturi în EA.
      </p>
    )
  }
  return (
    <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="mb-1 font-medium text-slate-700">Corelarea cu fișa din contabilitate</div>
      <table className="w-full">
        <tbody>
          <tr>
            <td className="py-0.5 text-slate-600">Sold de încasat (fișa de mai sus)</td>
            <td className="py-0.5 text-right tabular-nums">{lei(sold)}</td>
          </tr>
          <tr>
            <td className="py-0.5 text-slate-600">
              {diferenta > 0 ? '+' : '−'}{' '}
              {diferenta > 0
                ? 'Încasări intrate în bancă, nepuse încă pe facturi în EA'
                : 'Facturi ajunse la noi, neînregistrate încă în EA'}
            </td>
            <td className="py-0.5 text-right tabular-nums text-slate-600">
              {lei(Math.abs(diferenta))}
            </td>
          </tr>
          <tr className="border-t border-slate-300 font-semibold text-slate-800">
            <td className="py-1">= Sold contabil EA</td>
            <td className="py-1 text-right tabular-nums">{lei(soldEa)}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-2 text-[11px] text-slate-500">
        Rândul din mijloc e de lucru pentru contabilitate, nu o eroare: sunt operațiuni pe care o
        fișă le are și cealaltă nu. Documentele din spatele soldului contabil sunt listate mai jos.
      </p>
    </div>
  )
}

function FacturiEaTab({ d }: { d: FacturiEa }) {
  const zi = (s: string | null) => (s ? s.split('-').reverse().join('.') : '—')

  /*
    FISA DIN CONTABILITATE: cronologic, cu soldul dupa fiecare document.

    Tabul asta a fost pana pe 30.09.2026 si dovada din spatele cifrei de scadent — avea o cutie
    de calcul deasupra si coloanele „Stins" / „Ramas" la dreapta. Alexandru: „aici nu cred ca ar
    trebui sa se tina cont de scadenta, ci sa fie fisa de cont care sa se coreleze cu fisa din
    contabilitate."

    Avea dreptate de doua ori. Intai fiindca doua fise si un calcul de restanta intre ele nu se
    citesc impreuna. Apoi fiindca acea cutie venea dintr-un AL DOILEA calcul (`calculScadent`,
    prin `/client-facturi-ea`), nu din alocarea unica — exact sursa dubla din care iesise, cu
    cateva ore inainte, un scadent de 22.929 lei care nu exista. Dovada scadentului sta acum in
    tabul „Facturi neincasate", unde e si cifra.

    Ordinea e dupa data documentului. Cele FARA data — incasarile nealocate, pe care EA le
    raporteaza fara `docd` — merg la final: n-au un loc in timp, iar a le inventa unul ar muta
    soldul intermediar fara niciun temei.
  */
  const cronologic = [...d.documente].sort((a, b) => {
    if (!a.docDate) return 1
    if (!b.docDate) return -1
    return a.docDate.localeCompare(b.docDate) || a.docNo.localeCompare(b.docNo)
  })
  let cumulat = 0
  const randuri = cronologic.map((r) => {
    cumulat = Math.round((cumulat + r.remaining) * 100) / 100
    return { ...r, sold: cumulat }
  })
  /* Soldul final trebuie sa fie chiar soldul contabil. Daca nu e, se vede, nu se ascunde. */
  const diferentaSold = Math.round((cumulat - d.soldEa) * 100) / 100

  if (d.documente.length === 0) {
    return <div className="text-sm text-slate-500">Niciun document deschis în contabilitate.</div>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-2 py-1.5 text-left">Document</th>
            <th className="px-2 py-1.5 text-center">Data</th>
            <th className="px-2 py-1.5 text-center">Scadență</th>
            <th className="px-2 py-1.5 text-right">Debit</th>
            <th className="px-2 py-1.5 text-right">Credit</th>
            <th className="px-2 py-1.5 text-right">Sold</th>
          </tr>
        </thead>
        <tbody>
          {randuri.map((r) => (
            <tr key={r.docNo + r.docDate} className="border-b border-slate-100">
              <td className="px-2 py-1 text-slate-700">{r.docNo}</td>
              <td className="px-2 py-1 text-center text-slate-500">{zi(r.docDate)}</td>
              <td className="px-2 py-1 text-center text-slate-500">{zi(r.dueDate)}</td>
              {/*
                Debit si credit, ca in fisa din ERP. Pe CREDIT stau doar incasarile nealocate —
                EA le trimite fara data de document. O factura de storno e tot o factura: 4111
                e cont de activ, deci ramane pe DEBIT, cu minus (Alexandru, 06.10.2026).
              */}
              <td className="px-2 py-1 text-right">
                {r.docDate && r.remaining !== 0 ? lei(r.remaining) : '—'}
              </td>
              <td className="px-2 py-1 text-right text-emerald-700">
                {!r.docDate && r.remaining !== 0 ? lei(-r.remaining) : '—'}
              </td>
              <td className="px-2 py-1 text-right font-medium tabular-nums text-slate-700">
                {lei(r.sold)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-300 font-semibold">
            <td className="px-2 py-2" colSpan={5}>
              Sold contabil EA la {zi(d.laData)}
            </td>
            <td className="px-2 py-2 text-right tabular-nums text-slate-800">{lei(d.soldEa)}</td>
          </tr>
        </tfoot>
      </table>
      {/* Soldul cumulat trebuie sa se inchida pe soldul contabil. */}
      {Math.abs(diferentaSold) > 0.005 && (
        <p className="mt-2 text-[11px] text-rose-700">
          Soldul cumulat ({lei(cumulat)}) diferă cu {lei(diferentaSold)} de soldul contabil. Nu ar
          trebui — semnalează-mi.
        </p>
      )}
      {/*
        CE NU E fisa asta, si trebuie spus: `EaOpenInvoice` tine doar documentele DESCHISE.
        Facturile achitate si incasarile deja punctate nu ajung aici, deci cumulativul e
        descompunerea soldului de ACUM, nu istoricul complet al contului. A o lasa sa para
        altceva ar fi mai rau decat a n-o avea.
      */}
      <p className="mt-2 text-[11px] text-slate-500">
        Documentele <strong>deschise</strong> din contabilitate, în ordinea datei, cu soldul după
        fiecare. Facturile deja achitate și încasările punctate în EA nu apar — exportul pe care îl
        citim le trimite doar pe cele nestinse. Deci ultimul sold e chiar soldul contabil, dar
        rândurile nu sunt istoricul întreg al contului.
      </p>
    </div>
  )
}

function InvoiceDetail({
  client,
  asOf,
  startDate,
  onClose,
}: {
  client: { name: string; cif: string | null }
  asOf: string
  startDate: string
  onClose: () => void
}) {
  const [tab, setTab] = useState<'open' | 'compare' | 'ea'>('open')
  const [descarca, setDescarca] = useState(false)
  const [descarcaSold, setDescarcaSold] = useState(false)
  const reconQuery = useQuery<ReconResp>({
    queryKey: ['client-reconciliation', client.name, asOf, startDate],
    queryFn: async () =>
      (
        await api.get('/agents/client-reconciliation', {
          params: {
            name: client.name,
            cif: client.cif || undefined,
            asOf: asOf || undefined,
            startDate: startDate || undefined,
          },
          timeout: 60_000,
        })
      ).data,
    retry: false,
  })
  const recon = reconQuery.data

  // Tabul cu facturile CONTABILE. Se încarcă doar când e deschis: e o interogare în plus, iar
  // majoritatea deschiderilor se opresc la primul tab.
  const eaQuery = useQuery<FacturiEa>({
    queryKey: ['client-facturi-ea', client.name, client.cif, asOf, startDate],
    queryFn: async () =>
      (
        await api.get('/agents/client-facturi-ea', {
          params: {
            name: client.name,
            cif: client.cif || undefined,
            asOf: asOf || undefined,
            startDate: startDate || undefined,
          },
          timeout: 60_000,
        })
      ).data,
    enabled: tab === 'ea',
    retry: false,
  })
  const cifNum = (client.cif ?? '').replace(/\D/g, '')
  const q = useQuery<{ items: OpenInvoice[] }>({
    queryKey: ['client-invoices', client.cif, client.name],
    queryFn: async () =>
      (
        await api.get('/facturi-restante', {
          params: {
            kind: 'creante',
            status: 'all',
            b2bOnly: 'true',
            search: cifNum || client.name,
            limit: 500,
          },
          timeout: 60_000,
        })
      ).data,
  })
  const compareQuery = useQuery<CompareResp>({
    queryKey: ['client-compare', client.cif, client.name],
    enabled: tab === 'compare',
    queryFn: async () =>
      (
        await api.get('/agents/client-compare', {
          params: { cif: client.cif || undefined, name: client.name },
          timeout: 60_000,
        })
      ).data,
  })
  const items = (q.data?.items ?? [])
    .filter((i) => Math.abs(i.remainingAmount) > 0.005)
    .sort((a, b) => (a.daysToDue ?? 0) - (b.daysToDue ?? 0))
  const totalRest = items.reduce((s, i) => s + i.remainingAmount, 0)
  const STATUS_LABEL: Record<CompareRow['status'], { t: string; c: string }> = {
    ok: { t: 'OK', c: 'text-emerald-600' },
    only_ea: { t: 'lipsă în SPV local', c: 'text-amber-700 font-medium' },
    only_spv: { t: 'lipsă în EA local', c: 'text-amber-600' },
    value_diff: { t: 'dif. valoare', c: 'text-amber-700' },
    // Nu e o diferenta: factura e mai veche decat perioada descarcata din SPV.
    before_spv: { t: 'înainte de SPV local', c: 'text-slate-400' },
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <div className="font-semibold text-slate-800">{client.name}</div>
            <div className="text-xs text-slate-500">
              {recon
                ? `Sold reconciliat${recon.asOf ? ` la ${recon.asOf.split('-').reverse().join('.')}` : ''}: ${lei(recon.soldReconciled)}`
                : `Facturi neîncasate · de încasat: ${lei(totalRest)}`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => descarcaSoldFacturi(client, asOf, startDate, setDescarcaSold)}
              disabled={descarcaSold}
              className="rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              title="Situația facturilor neîncasate, cu termene — pentru trimis clientului"
            >
              {descarcaSold ? 'Se generează…' : '↓ Sold facturi (PDF)'}
            </button>
            {recon && (
              <button
                onClick={() => descarcaExtras(client, asOf, startDate, setDescarca)}
                disabled={descarca}
                className="rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Extras de cont PDF, pentru trimis clientului"
              >
                {descarca ? 'Se generează…' : '↓ Extras de cont (PDF)'}
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl px-2">
              ×
            </button>
          </div>
        </div>
        <div className="flex gap-1 px-5 pt-3 border-b border-slate-200">
          <button
            onClick={() => setTab('open')}
            className={`px-3 py-1.5 text-sm rounded-t-md ${tab === 'open' ? 'bg-slate-100 font-medium text-slate-800' : 'text-slate-500'}`}
          >
            Facturi neîncasate
          </button>
          <button
            onClick={() => setTab('compare')}
            className={`px-3 py-1.5 text-sm rounded-t-md ${tab === 'compare' ? 'bg-slate-100 font-medium text-slate-800' : 'text-slate-500'}`}
          >
            SPV vs EA
          </button>
          <button
            onClick={() => setTab('ea')}
            className={`px-3 py-1.5 text-sm rounded-t-md ${tab === 'ea' ? 'bg-slate-100 font-medium text-slate-800' : 'text-slate-500'}`}
            title="Fișa pe perioadă, lângă fișa documentelor deschise din contabilitate, cu diferența dintre ele explicată"
          >
            Fișă de cont
          </button>
        </div>

        {/* Tabul care face cifra verificabilă: „41.120 lei scadent" e o afirmație pe care
            agentul o repetă clientului la telefon. Dacă n-o poate urmări până la documentele
            care o compun, n-are ce căuta pe ecran. */}
        <div className={tab === 'ea' ? 'p-4' : 'hidden'}>
          {eaQuery.isLoading && <div className="text-sm text-slate-500">Se încarcă…</div>}
          {eaQuery.error && (
            <div className="text-sm text-rose-600">Nu am putut aduce facturile din EA.</div>
          )}
          {/*
            DOUA fise, si puntea dintre ele. Sus: pe perioada, cu incasarile din extrase — ce ai
            real de incasat. Jos: documentele deschise din contabilitate — fisa din EA. Intre ele,
            diferenta, numita: banii intrati in banca pe care contabilul inca nu i-a pus pe facturi.

            Pana pe 30.09.2026 intre cele doua statea o cutie de scadent, dintr-un al doilea calcul.
            A plecat in „Facturi neincasate", unde e si cifra (Alexandru: „aici nu cred ca ar trebui
            sa se tina cont de scadenta, ci sa fie fisa de cont care sa se coreleze cu fisa din
            contabilitate").
          */}
          {recon && <FisaDeCont recon={recon} />}
          {recon && eaQuery.data && (
            <PuntaContabilitate recon={recon} soldEa={eaQuery.data.soldEa} />
          )}
          {eaQuery.data && (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <div className="mb-2 text-sm font-medium text-slate-700">
                Fișa din contabilitate
                <span className="ml-2 font-normal text-[11px] text-slate-400">
                  documentele deschise în EA
                </span>
              </div>
              <FacturiEaTab d={eaQuery.data} />
            </div>
          )}
        </div>
        <div className={tab === 'open' ? 'p-4' : 'hidden'}>
          {recon && (
            <div className="mb-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="mb-2 font-medium text-slate-700">
                Sold reconciliat
                {recon.asOf ? ` la ${recon.asOf.split('-').reverse().join('.')}` : ' (azi)'}
              </div>
              <table className="w-full">
                <tbody>
                  <tr>
                    <td className="py-0.5 text-slate-600">
                      Sold la {(recon.startDate ?? recon.anchor).split('-').reverse().join('.')}
                    </td>
                    <td className="py-0.5 text-right tabular-nums">{lei(recon.soldPornire)}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5 text-slate-600">
                      + Facturi emise în perioadă ({recon.invoices.length})
                    </td>
                    <td className="py-0.5 text-right tabular-nums">{lei(recon.invoicesTotal)}</td>
                  </tr>
                  {/* „Data început" înaintea ancorei: până la ancoră încasările sunt cele din
                      contabilitate, de acolo încolo din extrase. Două surse, două rânduri. */}
                  {recon.eaSettled ? (
                    <>
                      <tr>
                        <td className="py-0.5 text-slate-600">
                          − Încasări înregistrate în EA (până la{' '}
                          {recon.anchor.split('-').reverse().join('.')})
                        </td>
                        <td className="py-0.5 text-right tabular-nums text-emerald-700">
                          {scade(recon.eaSettled)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-0.5 text-slate-600">
                          − Încasări din extrase (după {recon.anchor.split('-').reverse().join('.')}
                          )
                        </td>
                        <td className="py-0.5 text-right tabular-nums text-emerald-700">
                          {scade(recon.paymentsTotal - recon.eaSettled)}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td className="py-0.5 text-slate-600">
                        − Încasări în perioadă (extrase bancare)
                      </td>
                      <td className="py-0.5 text-right tabular-nums text-emerald-700">
                        −{lei(recon.paymentsTotal)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t border-slate-300 font-semibold text-slate-800">
                    <td className="py-1">= Sold de încasat</td>
                    <td className="py-1 text-right tabular-nums">{lei(recon.soldReconciled)}</td>
                  </tr>
                </tbody>
              </table>
              {/* Lista e a operațiunilor din EXTRAS: linia din contabilitate stă mai sus, pe rândul ei. */}
              <PaymentsList
                payments={recon.payments.filter((p) => p.sursa !== 'ea')}
                total={recon.paymentsTotal - (recon.eaSettled ?? 0)}
              />
              <div className="mt-2 text-[11px] text-slate-400">
                Mai jos, DIN CE E FORMAT soldul de mai sus: doar documentele neîncasate la{' '}
                {recon.asOf ? dmy(recon.asOf) : 'data de azi'}, cu încasările alocate în ordine
                (cele mai vechi întâi). Cele încasate integral nu apar. Totalul „Rest” e chiar
                soldul.
              </div>
            </div>
          )}
          {reconQuery.isLoading && <Spinner label="Se încarcă…" />}
          {recon && <ReconTable recon={recon} />}
          {/*
            O eroare NU e o ancora lipsa. Pana pe 06.10.2026 orice esec — de regula EA care nu
            raspundea la timp — se afisa „Seteaza ancora", desi ancora era setata.
          */}
          {reconQuery.isError && (
            <div className="text-sm text-red-700">
              ExpertAccounts nu a răspuns la timp. Închide fereastra și încearcă din nou peste un
              minut.
            </div>
          )}
          {!recon && !reconQuery.isLoading && !reconQuery.isError && (
            <div className="text-slate-400 text-sm">
              Setează ancora ca să vezi soldul reconciliat pe facturi.
            </div>
          )}
        </div>

        <div className={tab === 'compare' ? 'p-4' : 'hidden'}>
          {compareQuery.isLoading && <Spinner label="Se încarcă…" />}
          {compareQuery.data && (
            <>
              <div className="text-xs text-slate-500 mb-1">
                Facturat SPV: {lei(compareQuery.data.spvTotal)} · EA:{' '}
                {lei(compareQuery.data.eaTotal)} · lipsă în SPV local:{' '}
                <b className={compareQuery.data.onlyEa > 0 ? 'text-amber-700' : 'text-slate-500'}>
                  {compareQuery.data.onlyEa}
                </b>{' '}
                · lipsă în EA local: {compareQuery.data.onlySpv}
                {compareQuery.data.beforeSpv > 0 && (
                  <> · înainte de SPV local: {compareQuery.data.beforeSpv}</>
                )}
              </div>
              <div className="text-[11px] text-slate-400 mb-2">
                {compareQuery.data.spvCoverageFrom
                  ? `Copia locală din SPV începe la ${compareQuery.data.spvCoverageFrom.split('-').reverse().join('.')} — SPV-ul păstrează mesajele ~60 de zile, deci facturile emise înainte n-au avut de unde fi descărcate. Cele marcate „înainte de SPV local” nu sunt neconcordanțe.`
                  : 'Notă: SPV-ul local (descărcat din inbox) poate fi incomplet pentru facturi mai vechi — „lipsă în SPV local” nu înseamnă neapărat netrimisă, ci de verificat.'}
              </div>
              <table className="w-full text-sm">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-1.5">Factură</th>
                    <th className="text-left py-1.5">Dată</th>
                    <th className="text-right py-1.5">SPV</th>
                    <th className="text-right py-1.5">EA</th>
                    <th className="text-left py-1.5 pl-3">Stare</th>
                  </tr>
                </thead>
                <tbody>
                  {compareQuery.data.rows.map((r) => (
                    <tr
                      key={r.doc}
                      className={`border-b border-slate-100 ${r.status !== 'ok' ? 'bg-amber-50/40' : ''}`}
                    >
                      <td className="py-1.5 font-medium text-slate-700">{r.doc}</td>
                      <td className="py-1.5 text-slate-500">
                        {r.date ? new Date(r.date).toLocaleDateString('ro-RO') : '—'}
                      </td>
                      <td className="py-1.5 text-right">
                        {r.spvTotal != null ? lei(r.spvTotal) : '—'}
                      </td>
                      <td className="py-1.5 text-right">
                        {r.eaTotal != null ? lei(r.eaTotal) : '—'}
                      </td>
                      <td className={`py-1.5 pl-3 text-xs ${STATUS_LABEL[r.status].c}`}>
                        {STATUS_LABEL[r.status].t}
                      </td>
                    </tr>
                  ))}
                  {compareQuery.data.rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-slate-400 py-4">
                        Nicio factură găsită
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const AGING_SEGMENTS: { key: keyof Aging; label: string; color: string }[] = [
  { key: 'current', label: 'în termen', color: '#16a34a' },
  { key: 'd0_30', label: '0-30z', color: '#d9b500' },
  { key: 'd31_60', label: '31-60z', color: '#d97706' },
  { key: 'd61_90', label: '61-90z', color: '#ea580c' },
  { key: 'd90plus', label: '>90z', color: '#dc2626' },
]

function AgingBar({ aging }: { aging?: Aging | null }) {
  if (!aging) return <span className="text-xs text-slate-300">—</span>
  const segs = AGING_SEGMENTS.map((s) => ({ ...s, val: Math.max(0, aging[s.key]) }))
  const total = segs.reduce((s, x) => s + x.val, 0)
  if (total <= 0) return <span className="text-xs text-slate-300">—</span>
  const title = AGING_SEGMENTS.map(
    (s) => `${s.label}: ${Math.round(aging[s.key]).toLocaleString('ro-RO')}`,
  ).join(' · ')
  return (
    <div className="flex h-3 w-28 rounded overflow-hidden border border-slate-200" title={title}>
      {segs.map((s) =>
        s.val > 0 ? (
          <div key={s.key} style={{ width: `${(s.val / total) * 100}%`, background: s.color }} />
        ) : null,
      )}
    </div>
  )
}

/**
 * Cifra afisata vine din EA (contabilitate); insemnul arata cat difera de ce s-a declarat
 * la ANAF. O diferenta mare inseamna facturi emise si netrimise in e-Factura — sau, pentru
 * perioade dinainte de martie 2026, pur si simplu date SPV care lipsesc si nu mai pot fi
 * descarcate (ANAF listeaza doar ultimele 60 de zile).
 */
function EaBadge({ pct }: { pct: number | null }) {
  if (pct == null || pct < 15) return null
  return (
    <span
      title={`Diferență ${pct}% față de e-Factura (SPV) — facturi nedeclarate sau date SPV lipsă`}
      className="ml-1 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 align-middle"
    >
      ≠SPV {pct}%
    </span>
  )
}

function TargetBar({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-xs text-slate-400">—</span>
  const clamped = Math.min(100, Math.max(0, pct))
  const color = pct >= 80 ? '#16a34a' : pct >= 50 ? '#0b4f6c' : '#d97706'
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-slate-200 rounded">
        <div style={{ width: `${clamped}%`, background: color }} className="h-2 rounded" />
      </div>
      <span className="text-xs font-medium text-slate-600">{pct}%</span>
    </div>
  )
}

export function CockpitVanzariPage() {
  const [period, setPeriod] = useState<Period>('ytd')
  /** Care filă e deschisă. Valorile în lei rămân prima: e ecranul folosit zilnic. */
  const [fila, setFila] = useState<'valori' | 'cantitati' | 'profitabilitate'>('valori')
  /** Costurile si profitul: doar Victor si adminii (02.11.2026), pe drept separat de tablou. */
  const veziProfit = useAuthStore(selectHasPermission('vanzari.profitabilitate:read'))
  /*
    Filtrul de agent poate veni din adresa — linkul din emailul de incasari deschide tabloul pe
    agentul respectiv (`?agent=`). Se citeste o data, la deschidere; dupa aceea il schimba omul.
  */
  const [agent, setAgent] = useState(
    () => new URLSearchParams(window.location.search).get('agent') ?? '',
  )
  const eAdmin = useAuthStore((st) => st.user?.role) === 'admin'
  const [grup, setGrup] = useState('')
  const [onlyOverdue, setOnlyOverdue] = useState(false)
  const [onlyScadent, setOnlyScadent] = useState(false)
  const [q, setQ] = useState('')
  const [qDebounced, setQDebounced] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [allAgents, setAllAgents] = useState<string[]>([])
  const [invClient, setInvClient] = useState<{ name: string; cif: string | null } | null>(null)
  const [asOf, setAsOf] = useState('') // „Sold la data" — gol = azi
  // „Data început" — implicit prima zi a lunii anterioare. E fereastra pe care o vede
  // agentul: tot ce s-a întâmplat înainte e comprimat în „Sold pornire".
  const [startDate, setStartDate] = useState(() => {
    const n = new Date()
    const d = new Date(n.getFullYear(), n.getMonth() - 1, 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
  })
  const queryClient = useQueryClient()

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 350)
    return () => clearTimeout(t)
  }, [q])

  const query = useQuery<Cockpit>({
    queryKey: [
      'cockpit',
      period,
      agent,
      grup,
      onlyOverdue,
      onlyScadent,
      qDebounced,
      asOf,
      startDate,
    ],
    queryFn: async () =>
      (
        await api.get<Cockpit>('/agents/cockpit', {
          params: {
            period,
            agent: agent || undefined,
            grup: grup || undefined,
            onlyOverdue: onlyOverdue ? '1' : undefined,
            onlyScadent: onlyScadent ? '1' : undefined,
            q: qDebounced || undefined,
            asOf: asOf || undefined,
            startDate: startDate || undefined,
          },
          timeout: 90_000,
        })
      ).data,
  })

  const data = query.data
  const currentAnchor = data?.anchor ?? ''
  // `canSetAnchor` — decide selectorul de ancoră și badge-ul de divergență EA/SPV, care sunt
  // despre calitatea datelor. NU e „orice admin": serverul îl dă doar persoanei care verifică
  // față de balanță, după ce ancora a fost mutată de două ori de pe alte conturi de admin.
  const isAdmin = !!data?.canSetAnchor
  // Cifrele din contabilitate: nu toti adminii, doar persoana care face verificarea
  // fata de balanta. Serverul decide (`COCKPIT_ACCOUNTING_EMAILS`).
  const canSeeAccounting = !!data?.canSeeAccounting
  // Cifra „de încasat" pe care operatorul o VEDE pe rând: cea reconciliată când există
  // ancoră, cea contabilă altfel. Ordinea listei și evidențierea rândului se iau după ea —
  // altfel s-ar sorta după o coloană care nu se mai afișează.
  const deIncasat = (c: ClientRow): number => (data?.anchor ? (c.soldReconciled ?? 0) : c.restant)
  const sumBy = (pick: (c: ClientRow) => number | null | undefined): number =>
    (data?.clients ?? []).reduce((s, c) => s + (pick(c) ?? 0), 0)
  const soldReconciliatTotal = sumBy((c) => c.soldReconciled)

  // Ancora e globala: adminul o salveaza pentru toata lumea. Agentii nu vad selectorul.
  const anchorMut = useMutation({
    mutationFn: async (value: string) =>
      (await api.put('/agents/cockpit-anchor', { anchor: value || null })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cockpit'] })
      toast.success('Ancoră actualizată pentru toți')
    },
    onError: () => toast.error('Nu am putut salva ancora'),
  })

  /**
   * Salvarea ancorei trece prin confirmare.
   *
   * Pe 17.08.2026 ancora a trecut din dec. 2025 în iul. 2026 fără să știe nimeni de ce, de pe
   * un telefon — unde selectorul e o roată nativă și o atingere greșită nimerește opțiunea
   * vecină. Un `select` care salvează pe `change` transformă un gest de o secundă în mutarea
   * soldului de pornire pentru toți agenții.
   *
   * Dacă se refuză, nu se apelează nimic: selectorul e controlat de `currentAnchor`, deci
   * revine singur la valoarea din bază la următoarea randare.
   */
  const cereAncora = (value: string) => {
    const et = (v: string) => MONTH_ENDS.find((m) => m.value === v)?.label ?? 'fără reconciliere'
    if (!window.confirm(`Schimbi ancora din ${et(currentAnchor)} în ${et(value)} pentru TOȚI?`)) {
      return
    }
    anchorMut.mutate(value)
  }
  // Lista din selector se ia DOAR din răspunsul nefiltrat. Se completa o singură dată,
  // când era goală: dacă prima încărcare avea un filtru (căutare pe client, grup), lista
  // rămânea fixată pe agenții acelui rezultat și nu se mai putea alege altul.
  useEffect(() => {
    if (!data || agent || grup || qDebounced || onlyOverdue || onlyScadent) return
    const lista = data.agents.map((a) => a.agent).sort()
    setAllAgents((prev) =>
      prev.length === lista.length && prev.every((v, i) => v === lista[i]) ? prev : lista,
    )
  }, [data, agent, grup, qDebounced, onlyOverdue, onlyScadent])

  const clientsForAgent = useMemo(() => {
    if (!data) return new Map<string, ClientRow[]>()
    const m = new Map<string, ClientRow[]>()
    for (const c of data.clients) {
      const arr = m.get(c.agent) ?? []
      arr.push(c)
      m.set(c.agent, arr)
    }
    return m
  }, [data])

  function exportCsv() {
    if (!data) return
    const head = [
      'Client',
      'Agent',
      'Grup',
      'CIF',
      'Sell-in',
      'Restant',
      'Întârziere',
      'Target',
      '%',
      // Coloanele noi stau la FINAL, nu intercalate: cine are un fișier construit peste
      // exportul de până acum îl deschide mai departe fără să mute nimic.
      'Mod plată',
      'Scadent',
      'Din care fără termen',
      'Sell-out % buc',
      'Sell-out % val',
      'Stoc estimat buc',
      'Perioade fără raport',
    ]
    const rows = data.clients.map((c) => [
      c.name,
      c.agent,
      c.grup ?? '',
      c.cif ?? '',
      Math.round(c.sellIn),
      Math.round(c.restant),
      c.maxOverdueDays ?? '',
      Math.round(c.target),
      c.pctTarget ?? '',
      c.paymentMode === 'PE_MASURA_VANZARII' ? 'pe măsura vânzării' : 'la scadență',
      // Gol, nu 0, unde întrebarea nu se aplică: un zero într-o coloană de lei se adună.
      c.scadent != null ? Math.round(c.scadent) : '',
      c.faraTermen && c.faraTermen.count > 0 ? Math.round(c.faraTermen.amount) : '',
      c.sellout ? c.sellout.pctQty : '',
      c.sellout ? c.sellout.pctVal : '',
      c.sellout ? Math.round(c.sellout.stocEstimat) : '',
      c.sellout ? c.sellout.missingPeriods : '',
    ])
    const csv = [head, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cockpit-vanzari-${period}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Tablou de bord vânzări</h1>
          <p className={`text-slate-500 text-sm ${fila === 'profitabilitate' ? 'hidden' : ''}`}>
            Totaluri pe agent și client: facturat (din SPV), de încasat (soldul din ExpertAccounts,
            cu vechime) și target. Pentru sold la zi, apasă „Sincronizează sold” în Portofolii
            agenți (EA).
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={!data}
          className={`${fila === 'profitabilitate' ? 'hidden' : ''} px-3 py-2 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 text-sm`}
        >
          ⬇ Export CSV
        </button>
      </div>

      {/* Filtre — ale filelor Valori si Cantitati; Profitabilitatea are perioada ei. */}
      <div
        className={`${fila === 'profitabilitate' ? 'hidden' : 'flex'} flex-wrap gap-3 items-center bg-slate-50 border border-slate-200 rounded-lg p-3`}
      >
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className="px-3 py-2 rounded-md border border-slate-300 text-sm"
        >
          {PERIODS.map((p) => (
            <option key={p.v} value={p.v}>
              {p.label}
            </option>
          ))}
        </select>
        {/* Agentul de vânzări nu alege portofoliul: serverul i-l impune pe al lui. */}
        {data?.scope?.canFilter === false ? (
          data.scope.agent && (
            <span className="px-3 py-2 rounded-md bg-slate-100 text-sm text-slate-600">
              Portofoliul tău: <strong className="text-slate-800">{data.scope.agent}</strong>
            </span>
          )
        ) : (
          <select
            value={agent}
            onChange={(e) => setAgent(e.target.value)}
            className="px-3 py-2 rounded-md border border-slate-300 text-sm"
          >
            <option value="">Toți agenții</option>
            {/*
              Agentul venit din link (emailul de incasari) apare si cand lista e inca goala: ea se
              umple doar dintr-un raspuns NEFILTRAT. Fara asta, selectorul arata „Toti agentii"
              desi cifrele sunt ale agentului — si nu se mai putea alege altul.
            */}
            {(agent && !allAgents.includes(agent) ? [agent, ...allAgents] : allAgents).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        )}
        <select
          value={grup}
          onChange={(e) => setGrup(e.target.value)}
          className="px-3 py-2 rounded-md border border-slate-300 text-sm"
        >
          <option value="">Toate grupurile</option>
          {/*
            Din date, nu din cod. Lista era scrisă cu o singură valoare, „B2B", iar un grup nou
            în ERP — ANA-MARIA, 29.09.2026 — cerea o modificare de cod ca să fie vizibil, deși
            clienții îl aveau deja.
          */}
          {(data?.grupuri ?? []).map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={onlyOverdue}
            onChange={(e) => setOnlyOverdue(e.target.checked)}
          />
          Doar cu restanțe
        </label>
        {/* Întrebare diferită de cea de alături: „cu restanțe" înseamnă că mai are ceva de
            plătit, „cu scadent" că a și ajuns termenul. Se combină, nu se exclud. */}
        <label
          className="flex items-center gap-2 text-sm text-slate-600"
          title="Doar clienții care au facturi cu termenul depășit"
        >
          <input
            type="checkbox"
            checked={onlyScadent}
            onChange={(e) => setOnlyScadent(e.target.checked)}
          />
          Doar cu scadent
        </label>
        {data?.canSetAnchor && (
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <span className="whitespace-nowrap">Ancoră:</span>
            <select
              value={currentAnchor}
              onChange={(e) => cereAncora(e.target.value)}
              disabled={anchorMut.isPending}
              title="Sold reconciliat: ultima lună închisă în contabilitate. Se aplică tuturor."
              className="px-3 py-2 rounded-md border border-slate-300 text-sm"
            >
              <option value="">Fără reconciliere</option>
              {/* Eticheta „Ancoră:" a ieșit din opțiuni și a urcat lângă select. Cât timp toate
                  începeau cu același cuvânt, o tastă „a" apăsată din greșeală peste selectorul
                  cu focus sărea pe prima opțiune — iulie 2026 — și salva pentru toți. Acum
                  tastarea caută luna. */}
              {MONTH_ENDS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {data?.anchor && (
          <>
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <span className="whitespace-nowrap">Data început:</span>
              <DateField value={startDate} onChange={setStartDate} />
            </label>
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <span className="whitespace-nowrap">Sold la data:</span>
              <AsOfField value={asOf} onChange={setAsOf} coverage={data?.bankCoverageTo ?? null} />
            </label>
          </>
        )}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔍 caută client"
          className="px-3 py-2 rounded-md border border-slate-300 text-sm flex-1 min-w-[160px]"
        />
      </div>

      {/*
        Doua file, nu doua coloane in acelasi tabel. Dorian a cerut cantitatile per agent;
        Alexandru a cerut sa stea separat: „intr-un tab nou, sa nu se amestece cu valorile, ca
        ar fi greu de urmarit" (25.09.2026). Lei si bucati pe acelasi rand se citesc gresit
        exact cand te grabesti.
      */}
      <div className="flex gap-1 border-b border-slate-200">
        {(
          [
            ['valori', 'Valori (lei)'],
            ['cantitati', 'Cantități (buc)'],
            ...(veziProfit ? ([['profitabilitate', 'Profitabilitate']] as const) : []),
          ] as const
        ).map(([cheie, eticheta]) => (
          <button
            key={cheie}
            onClick={() => setFila(cheie)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${
              fila === cheie
                ? 'border-slate-800 font-medium text-slate-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {eticheta}
          </button>
        ))}
      </div>

      {/*
        FILTRUL DE AGENT merge si aici. Lipsea: fila cerea doar perioada, deci alegerea din capul
        ecranului nu avea niciun efect — ramaneau toti agentii, pe orice selectie (Alexandru,
        01.10.2026, cu filtrul pe Claudia si tabelul plin de toata lumea).

        Vizibilitatea n-a fost niciodata in joc: pentru un om de vanzari serverul ignora filtrul
        din adresa si pune portofoliul LUI (`resolveAgentScope`, testat). Ce lipsea era filtrul
        voit, al celui care are voie sa-l puna.
      */}
      {fila === 'cantitati' && <FilaCantitati period={period} agent={agent} />}
      {fila === 'profitabilitate' && veziProfit && <FilaProfitabilitate />}

      {fila === 'valori' && (
        <>
          {/*
        Soldul reconciliat = sold EA la ancoră + facturi − încasări din extrase. Peste
        ultima zi acoperită de extrase, încasările lipsesc: cifra iese mai mare decât
        realitatea. Extrasele se încarcă lunar, deci decalajul poate fi de săptămâni.
      */}
          {data?.anchor &&
            data.bankCoverageTo &&
            (!data.asOf || data.asOf > data.bankCoverageTo) && (
              <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
                <strong>Atenție la sold:</strong> e calculat{' '}
                {data.asOf ? `la ${data.asOf.split('-').reverse().join('.')}` : 'la zi'}, dar
                ultimul extras bancar încărcat merge doar până la{' '}
                <strong>{data.bankCoverageTo.split('-').reverse().join('.')}</strong>. Încasările
                primite după această dată nu sunt luate în calcul, deci soldul apare mai mare decât
                în realitate. Pentru cifra completă, apasă „ultimul extras” — sau încarcă extrasul
                următor.
              </div>
            )}
          {data?.scope?.unmapped && (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
              Contul tău nu e legat de un agent din ExpertAccounts, deci nu are portofoliu. Cere
              unui administrator să completeze numele agentului pe utilizator.
            </div>
          )}
          {query.isError && (
            <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3">
              Eroare la încărcare. Verifică dacă portofoliile sunt importate.
            </div>
          )}
          {query.isLoading && <Spinner label="Se încarcă tabloul de vânzări…" />}

          {data && (
            <>
              {/*
            Randul acesta e pentru verificare, nu pentru operare: langa soldul reconciliat
            sta cel contabil, iar diferenta dintre ele e ce urmarim. Agentul nu-l vede —
            ar reciti aceleasi cifre de doua ori (soldul de pornire si cel final apar
            oricum in descompunerea de dedesubt), plus doua marimi pe perioade diferite:
            „Facturat · clientii agentului" si „Incasat" sunt pe perioada selectata (An curent), restul pe
            fereastra Data inceput → Sold la data.
            Fara ancora nu exista descompunere, deci randul rămâne singura sinteza.
          */}
              {(canSeeAccounting || !data.anchor) && (
                <div
                  className={`grid grid-cols-2 gap-3 ${data.anchor ? (canSeeAccounting ? 'md:grid-cols-6' : 'md:grid-cols-5') : 'md:grid-cols-4'}`}
                >
                  {/* Punctul de pornire sta primul, la marginea din stanga: rândul se citește
                de la stânga la dreapta ca o poveste — de unde am plecat, ce s-a întâmplat,
                unde am ajuns. */}
                  {data.anchor && (
                    <Kpi
                      label={`Sold de pornire${data.startDate ? ` (${data.startDate.split('-').reverse().join('.')})` : ''}`}
                      value={lei(sumBy((c) => c.soldPornire))}
                    />
                  )}
                  <Kpi
                    label={ETICHETE_CIFRE.facturatClientiAgent.eticheta}
                    value={lei(data.kpi.sellIn)}
                    hint={ETICHETE_CIFRE.facturatClientiAgent.detaliu}
                    accent
                  />
                  <Kpi
                    label={ETICHETE_CIFRE.incasat.eticheta}
                    value={lei(data.kpi.incasat)}
                    hint={ETICHETE_CIFRE.incasat.detaliu}
                  />
                  {data.anchor ? (
                    <Kpi
                      label={`De încasat la ${(data.asOf ?? new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Bucharest' })).split('-').reverse().join('.')}`}
                      value={lei(soldReconciliatTotal)}
                      hint={ETICHETE_CIFRE.deIncasat.detaliu}
                      warn={soldReconciliatTotal > 0}
                      accent
                    />
                  ) : (
                    <Kpi
                      label="De încasat"
                      value={lei(data.kpi.restant)}
                      hint={ETICHETE_CIFRE.deIncasat.detaliu}
                      warn={data.kpi.restant > 0}
                      accent
                    />
                  )}
                  {/* Cifra pentru care s-a cerut tot calculul: cat se poate cere ACUM, nu cat
                  are clientul de platit in total. Sta lipita de „De incasat", pentru ca doar
                  alaturi de ea inseamna ceva. */}
                  <Kpi
                    label="Din care scadent"
                    value={lei(data.kpi.scadent)}
                    warn={data.kpi.scadent > 0}
                    hint={[
                      `din ${data.kpi.clientiScadenta} ${data.kpi.clientiScadenta === 1 ? 'client la scadență' : 'clienți la scadență'}`,
                      data.kpi.clientiPeMasuraVanzarii > 0
                        ? `${data.kpi.clientiPeMasuraVanzarii} pe măsura vânzării, în afara cifrei`
                        : null,
                      data.kpi.clientiFaraTermen > 0
                        ? `include facturi fără termen agreat la ${data.kpi.clientiFaraTermen}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                  <Kpi
                    label="Target realizat"
                    value={data.kpi.pctTarget != null ? `${data.kpi.pctTarget}%` : '—'}
                  />
                  {/* Soldul din contabilitate sta aici, langa cel reconciliat, ca sa se vada
                diferenta dintr-o privire. Il vede o singura persoana — e verificare fata
                de balanta, nu ceva de folosit cand suni clientul. */}
                  {canSeeAccounting && data.anchor && (
                    <Kpi
                      label={
                        data.asOf
                          ? `Sold contabil EA la ${data.asOf.split('-').reverse().join('.')}`
                          : data.soldAsOf
                            ? `Sold contabil EA (${new Date(data.soldAsOf).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})`
                            : 'Sold contabil EA'
                      }
                      value={lei(data.asOf ? sumBy((c) => c.soldContabilAsOf) : data.kpi.restant)}
                    />
                  )}
                </div>
              )}

              {/*
            Descompunerea e vizibila TUTUROR: soldul de la ancora e punctul de pornire al
            calculului, iar fara el cifra de sus n-are explicatie. Doar soldul contabil la
            zi ramane pentru admin — acela e verificarea fata de balanta, nu ceva ce-i
            trebuie agentului cand suna clientul.
          */}
              {data.anchor && (
                <div>
                  <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                    {canSeeAccounting ? 'Cum se obține soldul de mai sus' : 'Soldul de încasat'}
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <Kpi
                      label={`Sold de pornire${data.startDate ? ` (${data.startDate.split('-').reverse().join('.')})` : ''}`}
                      value={lei(sumBy((c) => c.soldPornire))}
                    />
                    <Kpi label="+ Facturi emise" value={lei(sumBy((c) => c.facturiPerioada))} />
                    <Kpi
                      label={
                        data.startDinEa ? '− Încasări (EA + extrase)' : '− Încasări din extrase'
                      }
                      value={lei(sumBy((c) => c.incasariPerioada))}
                      hint={
                        data.startDinEa && data.anchor
                          ? `din care ${lei(sumBy((c) => c.incasariEa ?? 0))} înregistrate în EA până la ${data.anchor.split('-').reverse().join('.')}, restul din extrase`
                          : undefined
                      }
                    />
                    <Kpi label="= Sold final" value={lei(soldReconciliatTotal)} accent />
                  </div>
                </div>
              )}

              {/* Cifre de portofoliu, nu de bani — stau separat ca sa nu se amestece cu
              plimbarea soldului. Le vad si agentii: sunt despre clientii lor. */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Kpi label="Clienți activi" value={String(data.kpi.clientsActive)} />
                <Kpi
                  label="Clienți cu întârziere"
                  value={String(data.kpi.overdueClients)}
                  warn={data.kpi.overdueClients > 0}
                />
              </div>
              {!!data.kpi.inValuta?.length && (
                <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                  Conține valută:{' '}
                  {data.kpi.inValuta
                    .map(
                      (v) =>
                        `${v.count} facturi în ${v.moneda}` +
                        (v.faraCurs ? ` (${v.faraCurs} fără curs BNR, neincluse)` : ''),
                    )
                    .join(', ')}{' '}
                  (e-Factura) — incluse în sume în lei, la cursul facturii.
                </p>
              )}

              {/* Leaderboard agenți */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700 text-white">
                    <tr>
                      <th className="text-left px-3 py-2">Agent</th>
                      <th className="text-right px-3 py-2">Clienți</th>
                      <th
                        className="text-right px-3 py-2"
                        title={`Facturat · clienții agentului · ${ETICHETE_CIFRE.facturatClientiAgent.detaliu}`}
                      >
                        Facturat
                      </th>
                      <th className="text-center px-3 py-2">6 luni</th>
                      <th className="text-right px-3 py-2" title={ETICHETE_CIFRE.deIncasat.detaliu}>
                        De încasat
                      </th>
                      <th className="text-right px-3 py-2" title="Facturi cu termenul depășit">
                        Scadent
                      </th>
                      <th className="text-center px-3 py-2">Întârz.</th>
                      <th className="text-left px-3 py-2">Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.agents.map((a) => (
                      <RowGroup
                        key={a.agent}
                        a={a}
                        expanded={expanded === a.agent}
                        onToggle={() => setExpanded(expanded === a.agent ? null : a.agent)}
                        clients={clientsForAgent.get(a.agent) ?? []}
                        onClientClick={(c) => setInvClient({ name: c.name, cif: c.cif })}
                      />
                    ))}
                    {data.agents.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center text-slate-400 py-4">
                          Niciun agent (importă portofoliile întâi)
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Tabel clienți (vizibil permanent, sortat după de încasat) */}
              <div>
                <h2 className="font-semibold text-slate-700 mb-2">
                  Clienți ({data.clients.length}){' '}
                  <span className="text-xs font-normal text-slate-400">
                    — click pe client pentru facturile neîncasate
                  </span>
                </h2>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2">Client</th>
                        <th className="text-left px-3 py-2">Agent</th>
                        <th
                          className="text-center px-3 py-2"
                          title="Termenul de plată din EA. PMV = pe măsura vânzării (termenul de 90 de zile)"
                        >
                          Termen
                        </th>
                        {/* Cele trei cifre se citesc ca o singura fraza: de la cat am pornit,
                        cat am facturat de atunci, cat s-a incasat — si ce ramane. */}
                        {data.anchor && (
                          <>
                            <th className="text-right px-3 py-2">
                              Sold pornire
                              {data.startDate
                                ? ` (${data.startDate.split('-').reverse().join('.')})`
                                : ''}
                            </th>
                            <th className="text-right px-3 py-2">+ Facturi emise</th>
                            <th className="text-right px-3 py-2">− Încasări</th>
                          </>
                        )}
                        {!data.anchor && (
                          <>
                            <th
                              className="text-right px-3 py-2"
                              title={ETICHETE_CIFRE.facturatClientiAgent.detaliu}
                            >
                              Facturat
                            </th>
                            <th
                              className="text-right px-3 py-2"
                              title={ETICHETE_CIFRE.incasat.detaliu}
                            >
                              Încasat
                            </th>
                          </>
                        )}
                        {/* Cand exista ancora, soldul contabil se vede o singura data, in cifra
                        de sus („Sold contabil EA la ..."). Pe rand ar fi doar aceeasi cifra
                        a doua oara. Fara ancora insa, asta E singurul „De incasat". */}
                        {!data.anchor && (
                          <th
                            className="text-right px-3 py-2"
                            title={ETICHETE_CIFRE.deIncasat.detaliu}
                          >
                            De încasat
                          </th>
                        )}
                        {data.anchor && (
                          <th className="text-right px-3 py-2 bg-sky-50">
                            De încasat
                            {data.asOf ? ` la ${data.asOf.split('-').reverse().join('.')}` : ''}
                          </th>
                        )}
                        <th className="text-right px-3 py-2" title="Facturi cu termenul depășit">
                          Scadent
                        </th>
                        <th className="text-center px-3 py-2">Vechime sold</th>
                        <th className="text-center px-3 py-2">Întârz.</th>
                        <th className="text-left px-3 py-2">Target</th>
                        <th className="text-center px-3 py-2">Ultima cmd</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.clients]
                        .sort((a, b) => deIncasat(b) - deIncasat(a))
                        .map((c) => (
                          <tr
                            key={`${c.name}|${c.agent}`}
                            onClick={() => setInvClient({ name: c.name, cif: c.cif })}
                            className={`border-b border-slate-100 cursor-pointer hover:bg-sky-50 ${deIncasat(c) > 0 ? 'bg-amber-50/40' : ''}`}
                          >
                            <td className="px-3 py-1.5 text-slate-800">
                              {c.name}
                              {c.grup ? (
                                <span className="ml-2 text-[10px] text-slate-400">{c.grup}</span>
                              ) : null}
                              <FostClient c={c} />
                            </td>
                            <td className="px-3 py-1.5 text-slate-500">{c.agent}</td>
                            <td className="px-3 py-1.5 text-center text-xs text-slate-600">
                              <TermenPlata c={c} />
                            </td>
                            {data.anchor && (
                              <>
                                <td className="px-3 py-1.5 text-right text-slate-600">
                                  {c.soldPornire != null ? lei(c.soldPornire) : '—'}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  {lei(c.facturiPerioada)}
                                  {isAdmin ? <EaBadge pct={c.discrepancyPct} /> : null}
                                </td>
                                <td className="px-3 py-1.5 text-right text-slate-500">
                                  {lei(c.incasariPerioada)}
                                </td>
                              </>
                            )}
                            {!data.anchor && (
                              <>
                                <td className="px-3 py-1.5 text-right">
                                  {lei(c.sellIn)}
                                  <EaBadge pct={c.discrepancyPct} />
                                </td>
                                <td className="px-3 py-1.5 text-right text-slate-500">
                                  {lei(c.incasat)}
                                </td>
                              </>
                            )}
                            {!data.anchor && (
                              <td
                                className={`px-3 py-1.5 text-right ${c.restant > 0 ? 'text-amber-700 font-medium' : 'text-slate-400'}`}
                                title={
                                  c.restantSource === 'ea' ? 'Sold EA (autoritar)' : 'SPV+bancă'
                                }
                              >
                                {lei(c.restant)}
                              </td>
                            )}
                            {data.anchor && (
                              <td
                                className="px-3 py-1.5 text-right font-medium bg-sky-50/40"
                                title={
                                  c.soldReconciled != null
                                    ? `sold pornire ${Math.round(c.soldPornire ?? 0).toLocaleString('ro-RO')} + facturi ${Math.round(c.facturiPerioada).toLocaleString('ro-RO')} − încasări ${Math.round(c.incasariPerioada).toLocaleString('ro-RO')}`
                                    : ''
                                }
                              >
                                {c.soldReconciled != null ? lei(c.soldReconciled) : '—'}
                              </td>
                            )}
                            <ScadentCell c={c} />
                            <td className="px-3 py-1.5">
                              <div className="flex justify-center">
                                {/* Bara de vechime presupune scadențe. La clienții pe măsura vânzării
                                n-are ce arăta, așa că locul îl ia evoluția vânzării lor. */}
                                {c.paymentMode === 'PE_MASURA_VANZARII' ? (
                                  <Spark data={(c.sellout?.monthly ?? []).map((m) => m.qtyOut)} />
                                ) : (
                                  <AgingBar aging={c.aging} />
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              <OverdueDot days={c.maxOverdueDays} />
                            </td>
                            <td className="px-3 py-1.5">
                              <TargetBar pct={c.pctTarget} />
                            </td>
                            <UltimaComanda c={c} />
                          </tr>
                        ))}
                      {data.clients.length === 0 && (
                        <tr>
                          <td
                            colSpan={data.anchor ? 12 : 11}
                            className="text-center text-slate-400 py-4"
                          >
                            Niciun client
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {invClient && (
            <InvoiceDetail
              client={invClient}
              asOf={asOf}
              startDate={startDate}
              onClose={() => setInvClient(null)}
            />
          )}
        </>
      )}
      {eAdmin && <EmailIncasariCard />}
    </div>
  )
}

function Kpi({
  label,
  value,
  accent,
  warn,
  hint,
}: {
  label: string
  value: string
  accent?: boolean
  warn?: boolean
  /** Rând mic sub cifră: ce NU e cuprins în ea. Fără el, cifra pare să acopere tot. */
  hint?: string
}) {
  return (
    <div
      className={`rounded-lg border px-4 py-3 ${
        accent
          ? 'border-sky-300 bg-sky-50'
          : warn
            ? 'border-amber-300 bg-amber-50'
            : 'border-slate-200'
      }`}
    >
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-800 mt-0.5">{value}</div>
      {hint ? <div className="mt-0.5 text-[10px] leading-tight text-slate-400">{hint}</div> : null}
    </div>
  )
}

function RowGroup({
  a,
  expanded,
  onToggle,
  clients,
  onClientClick,
}: {
  a: AgentRow
  expanded: boolean
  onToggle: () => void
  clients: ClientRow[]
  onClientClick: (c: ClientRow) => void
}) {
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={onToggle}>
        <td className="px-3 py-2 font-medium text-slate-800">
          <span className="inline-block w-4 text-slate-400">{expanded ? '▾' : '▸'}</span>
          {a.agent}
        </td>
        <td className="px-3 py-2 text-right">{a.clientsCount}</td>
        <td className="px-3 py-2 text-right font-semibold">
          {lei(a.sellIn)}
          <EaBadge pct={a.discrepancyPct} />
        </td>
        <td className="px-3 py-2">
          <div className="flex justify-center">
            <Spark data={a.spark} />
          </div>
        </td>
        {/* Cand exista ancora, aici merge soldul RECONCILIAT — acelasi pe care il arata
            si clientii dedesubt si care reacționeaza la „Sold la data". Cifra contabila
            nu se recalculeaza la o data trecuta, deci pe rândul agentului părea că
            filtrul de dată n-are efect. */}
        {(() => {
          const val = a.soldReconciled ?? a.restant
          return (
            <td className={`px-3 py-2 text-right ${val > 0 ? 'text-amber-700 font-medium' : ''}`}>
              {lei(val)}
            </td>
          )
        })()}
        <td
          className={`px-3 py-2 text-right ${(a.scadent ?? 0) > 0 ? 'font-semibold text-rose-700' : 'text-slate-400'}`}
        >
          {(a.scadent ?? 0) > 0 ? lei(a.scadent!) : '—'}
        </td>
        <td className="px-3 py-2 text-center">
          <OverdueDot days={a.maxOverdueDays} />
        </td>
        <td className="px-3 py-2">
          <TargetBar pct={a.pctTarget} />
        </td>
      </tr>
      {expanded &&
        clients.map((c) => (
          <tr
            key={`${c.name}|${c.agent}`}
            onClick={() => onClientClick(c)}
            className="border-b border-slate-50 bg-slate-50/60 text-[13px] cursor-pointer hover:bg-sky-50"
          >
            <td className="px-3 py-1.5 pl-10 text-slate-700">
              {c.name}
              {c.grup ? <span className="ml-2 text-[10px] text-slate-400">{c.grup}</span> : null}
              <FostClient c={c} />
            </td>
            <td className="px-3 py-1.5"></td>
            <td className="px-3 py-1.5 text-right">{lei(c.sellIn)}</td>
            <UltimaComanda c={c} />
            <td className={`px-3 py-1.5 text-right ${c.restant > 0 ? 'text-amber-700' : ''}`}>
              {lei(c.restant)}
            </td>
            <ScadentCell c={c} small />
            <td className="px-3 py-1.5 text-center">
              <OverdueDot days={c.maxOverdueDays} />
            </td>
            <td className="px-3 py-1.5">
              <TargetBar pct={c.pctTarget} />
            </td>
          </tr>
        ))}
    </>
  )
}

/** Un produs vândut, în bucăți. Codul vine din EA (`info3`) și e cerut pentru identificare. */
interface ProdusCantitate {
  sku: string
  denumire: string
  bucati: number
  facturi: number
}

interface LunaCantitati {
  luna: string
  bucati: number
  pctTinta: number | null
}

interface AgentCantitati {
  agent: string
  bucati: number
  /** Media pe lună din perioada aleasă. Ea se compară cu ținta, nu totalul. */
  medieLunara: number
  /** Luna cu lună — raportul propriu-zis. */
  luniDetaliu: LunaCantitati[]
  /** Ținta LUNARĂ. Nu se înmulțește cu lunile perioadei. */
  tinta: number
  pctTinta: number | null
  produse: ProdusCantitate[]
}

interface RaportCantitati {
  luni: number
  /** Capetele de coloană ale grilei, `AAAA-LL`. */
  etichetLuni: string[]
  tintaLunara: number
  agenti: AgentCantitati[]
  total: number
}

/**
 * Fila „Cantități": câte bucăți a vândut fiecare agent, și din ce produse.
 *
 * Dorian, 25.09.2026: „se poate face o coloană cu cantitățile vândute per agent? Să fie mai
 * ușor de urmărit obiectivele cantitative". Obiectivele sunt în bucăți — 1000 pe lună de agent —
 * deci în lei nu se pot urmări deloc.
 *
 * Agentul se desface la clic, nu din start: cu șase agenți × paisprezece produse, tot deschis ar
 * fi o listă de optzeci de rânduri în care nu se mai vede cine unde stă.
 */
function FilaCantitati({ period, agent }: { period: Period; agent: string }) {
  const [desfacut, setDesfacut] = useState<string | null>(null)
  const q = useQuery<RaportCantitati>({
    /* Agentul intra SI in cheie: altfel raspunsul filtrat ar fi servit din cache celui nefiltrat. */
    queryKey: ['cockpit-cantitati', period, agent],
    queryFn: async () =>
      (
        await api.get<RaportCantitati>('/agents/cockpit/cantitati', {
          params: { period, agent: agent || undefined },
        })
      ).data,
  })

  if (q.isLoading) return <Spinner label="Se numără bucățile…" />
  if (q.isError)
    return (
      <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3">
        Eroare la încărcare.
      </div>
    )

  const d = q.data
  if (!d || d.agenti.length === 0)
    return <div className="text-slate-500 text-sm py-6">Nicio vânzare în perioada aleasă.</div>

  return (
    <div className="space-y-3">
      {/*
        Tinta e LUNARA si ramane lunara, oricare ar fi perioada. Prima varianta o inmultea cu
        lunile — 9000 pe „anul curent" — iar Alexandru a corectat: „tinta era de 1000 buc/luna,
        nu 9000". Cifra pe care o stie omul din cap trebuie sa fie cifra de pe ecran; de-aia se
        compara media lunara, nu totalul.
      */}
      <p className="text-sm text-slate-500">
        Bucăți ieșite pe facturi, din liniile din ExpertAccounts, <strong>lună cu lună</strong>.
        Ținta e <strong>{d.tintaLunara.toLocaleString('ro-RO')} buc/lună</strong> de agent; culoarea
        cifrei spune dacă luna aceea a atins-o. Stornourile scad.
      </p>
      {/*
        Media se citeste cu grija la cine n-a lucrat toate lunile: se imparte la TOATE lunile
        perioadei. Alin Simut, angajat de doua-trei luni, apare cu o medie mica pe „anul curent"
        nu pentru ca ar fi vandut putin, ci pentru ca se imparte la noua (Alexandru, 28.09.2026).
        Randul lunar e adevarul; media e doar un rezumat.
      */}
      <p className="text-xs text-slate-400">
        „Medie/lună” se calculează pe toate lunile perioadei, inclusiv cele dinaintea angajării
        agentului. Pentru agenții angajați în cursul anului, urmărește rândul lunar, nu media.
      </p>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Agent</th>
              {/*
                O coloană pe lună. Asta e raportul: „situația trebuie făcută lunar" (Alexandru,
                28.09.2026). O singură cifră pe perioadă ascunde exact ce interesează — cine a
                scăzut, cine a crescut, și cine n-a lucrat toate lunile.
              */}
              {d.etichetLuni.map((m) => (
                <th key={m} className="px-2 py-2 text-right font-medium whitespace-nowrap">
                  {numeLuna(m)}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-medium border-l border-slate-200">Total</th>
              <th className="px-3 py-2 text-right font-medium">Medie/lună</th>
              <th className="px-3 py-2 text-right font-medium">Țintă/lună</th>
            </tr>
          </thead>
          <tbody>
            {d.agenti.map((a) => (
              <Fragment key={a.agent}>
                <tr
                  onClick={() => setDesfacut(desfacut === a.agent ? null : a.agent)}
                  className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-3 py-2 font-medium text-slate-800">
                    <span className="text-slate-400 mr-1.5">
                      {desfacut === a.agent ? '▾' : '▸'}
                    </span>
                    {a.agent}
                  </td>
                  {a.luniDetaliu.map((l) => (
                    <CelulaLuna key={l.luna} luna={l} areTinta={a.tinta > 0} />
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums font-medium border-l border-slate-200">
                    {a.bucati.toLocaleString('ro-RO')}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {a.medieLunara.toLocaleString('ro-RO')}
                  </td>
                  {/*
                    Zero inseamna „fara tinta", nu „tinta zero", si se scrie ca atare. Victor are
                    pe portofoliu clientii mari, eMAG inclus: o tinta de agent de teren l-ar arata
                    la mii la suta, adica o cifra care nu spune nimic despre nimeni (Alexandru,
                    28.09.2026: „Victor — nu are target").
                  */}
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                    {a.tinta > 0 ? a.tinta.toLocaleString('ro-RO') : '—'}
                  </td>
                </tr>
                {desfacut === a.agent &&
                  a.produse.map((p) => (
                    <tr key={p.sku} className="bg-slate-50/60 text-slate-600">
                      {/*
                        Produsele sunt ale PERIOADEI, nu ale unei luni: desfăcute și pe luni ar
                        da o grilă de paisprezece produse × nouă luni, pe care n-o citește nimeni.
                      */}
                      <td className="px-3 py-1.5 pl-9" colSpan={d.etichetLuni.length + 1}>
                        {/* Codul, cerut de Alexandru: denumirile se aseamănă între gramaje. */}
                        <span className="font-mono text-xs text-slate-500 mr-2">{p.sku}</span>
                        {p.denumire}
                        <span className="ml-2 text-xs text-slate-400">
                          {p.facturi} {p.facturi === 1 ? 'factură' : 'facturi'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums border-l border-slate-200">
                        {p.bucati.toLocaleString('ro-RO')}
                      </td>
                      <td />
                      <td />
                    </tr>
                  ))}
              </Fragment>
            ))}
            <tr className="border-t-2 border-slate-300 font-semibold bg-slate-50">
              <td className="px-3 py-2">Total</td>
              {d.etichetLuni.map((m) => (
                <td key={m} className="px-2 py-2 text-right tabular-nums">
                  {d.agenti
                    .reduce((s, a) => s + (a.luniDetaliu.find((l) => l.luna === m)?.bucati ?? 0), 0)
                    .toLocaleString('ro-RO')}
                </td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums border-l border-slate-200">
                {d.total.toLocaleString('ro-RO')}
              </td>
              <td />
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** `2026-09` → `sep`. Capetele de coloană trebuie să încapă pe lățimea unei cifre de patru. */
const LUNI_SCURT = [
  'ian',
  'feb',
  'mar',
  'apr',
  'mai',
  'iun',
  'iul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
]

function numeLuna(m: string): string {
  const [an, luna] = m.split('-')
  const nume = LUNI_SCURT[Number(luna) - 1] ?? m
  // Anul apare doar în ianuarie: altfel se repetă de nouă ori degeaba.
  return luna === '01' ? `${nume} ${an.slice(2)}` : nume
}

/**
 * O lună dintr-un rând de agent.
 *
 * Culoarea spune dacă luna aceea a atins ținta, fără să citești procentul: verde peste 100%,
 * albastru de la jumătate în sus, chihlimbar sub. La cine n-are țintă rămâne cifra simplă —
 * nu există față de ce să fie colorată.
 *
 * Zero se scrie cu gri deschis, nu se ascunde: o lună fără vânzări e o informație, nu un gol.
 */
function CelulaLuna({ luna, areTinta }: { luna: LunaCantitati; areTinta: boolean }) {
  if (luna.bucati === 0) {
    return <td className="px-2 py-2 text-right tabular-nums text-slate-300">0</td>
  }
  const culoare = !areTinta
    ? 'text-slate-600'
    : (luna.pctTinta ?? 0) >= 100
      ? 'text-green-700 font-medium'
      : (luna.pctTinta ?? 0) >= 50
        ? 'text-slate-700'
        : 'text-amber-700'
  return (
    <td
      className={`px-2 py-2 text-right tabular-nums ${culoare}`}
      title={luna.pctTinta == null ? 'fără țintă' : `${luna.pctTinta}% din țintă`}
    >
      {luna.bucati.toLocaleString('ro-RO')}
    </td>
  )
}

const SURSA_COMANDA = {
  crm: 'comandă din CRM',
  erp: 'comandă din ERP',
  factura: 'factură emisă',
} as const

/** „Ultima cmd": cea mai recentă dintre comenzile CRM trimise, comenzile ERP și facturi. */
function UltimaComanda({ c }: { c: Pick<ClientRow, 'lastOrderAt' | 'lastOrderSource'> }) {
  return (
    <td
      className="px-3 py-1.5 text-center text-slate-400"
      title={c.lastOrderSource ? `Ultima: ${SURSA_COMANDA[c.lastOrderSource]}` : undefined}
    >
      {c.lastOrderAt ? new Date(c.lastOrderAt).toLocaleDateString('ro-RO') : '—'}
    </td>
  )
}
